import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import { usePostHog } from "posthog-js/react";
import DiscountOfferModal from "../components/DiscountOfferModal";
import LoadingSpinner from "./LoadingSpinner";
import { getSkinSrc } from "../components/SeasonalSkins";
import { getNameStyleCSS } from "./GameNameStyles";
import {
  useTonConnectUI,
  useTonWallet,
} from "@tonconnect/ui-react";
import "../index.css";

// Normalize "user has gamepass" from whatever the backend puts in the user
// SUPER DEFENSIVE: try really hard to see "gamepass" on the user object
const userHasGamepass = (u, depth = 0) => {
  if (!u || typeof u !== "object" || depth > 2) return false;

  for (const [key, value] of Object.entries(u)) {
    const keyLower = key.toLowerCase();

    // any key that mentions "gamepass" is suspicious
    if (keyLower.includes("gamepass")) {
      if (typeof value === "boolean") {
        if (value) return true;
      } else if (typeof value === "number") {
        if (value > 0) return true;
      } else if (typeof value === "string") {
        const s = value.toLowerCase().trim();
        if (["true", "1", "active", "enabled", "yes"].includes(s)) {
          return true;
        }
      } else if (value && typeof value === "object") {
        // nested: e.g. user.gamepass = { active: true }
        if (userHasGamepass(value, depth + 1)) return true;
      }
    }

    // also accept generic "has_pass"/"pass_active" with no "gamepass" word
    if (keyLower === "has_pass" || keyLower === "pass_active") {
      if (typeof value === "boolean" && value) return true;
      if (typeof value === "number" && value > 0) return true;
      if (typeof value === "string") {
        const s = value.toLowerCase().trim();
        if (["true", "1", "active", "enabled", "yes"].includes(s)) {
          return true;
        }
      }
    }
  }

  return false;
};

const ShopHubTitle = ({ children }) => (
  <div
    className="modal_title modal_title_special"
  >
    {children}
  </div>
);

const ShopHubTitle2 = ({ children }) => (
  <div
    className="modal_title"
  >
    {children}
  </div>
);

const ShopHubTitle3 = ({ children }) => (
  <div
    className="modal_title modal_title_special_2"
  >
    {children}
  </div>
);

const ShopHubTitle4 = ({ children }) => (
  <div
    className="modal_title modal_title_special_3"
  >
    {children}
  </div>
);

const ShopHubTitle5 = ({ children }) => (
  <div
    className="modal_title modal_title_special_4"
  >
    {children}
  </div>
);

const ShopHubBanner = ({
  bg,
  title,
  subtitle,
  button = "ENTER",
  badge,
  image,
  imageStyle = {},
  onClick,
  height = 132,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    onClick={onClick}
    style={{
      width: "100%",
      height,
      margin: "0 auto 10px",
      borderRadius: 8,
      border: "3px solid #7b300e",
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
      backgroundImage: `url('${bg}')`,
      backgroundSize: "100% 100%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      boxShadow: "0 3px 0 rgba(0,0,0,0.35)",
    }}
  >
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(90deg, rgba(80,18,4,0.86) 0%, rgba(160,55,15,0.55) 55%, rgba(80,18,4,0.1) 100%)",
        pointerEvents: "none",
      }}
    />

    {image && (
      <img
        src={image}
        alt=""
        draggable={false}
        style={{
          position: "absolute",
          left: 8,
          bottom: 0,
          height: "105%",
          objectFit: "contain",
          zIndex: 2,
          pointerEvents: "none",
          ...imageStyle,
        }}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    )}

    {badge && (
      <div
        className="shadow1"
        style={{
          position: "absolute",
          right: 8,
          top: 8,
          width: 58,
          height: 45,
          borderRadius: 6,
          background: "#f04b23",
          border: "3px solid #ffd85b",
          color: "#fff",
          fontSize: 18,
          lineHeight: "18px",
          fontWeight: 900,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          transform: "rotate(-5deg)",
          zIndex: 4,
        }}
      >
        {badge}
      </div>
    )}

    <div
      style={{
        position: "absolute",
        left: image ? 132 : 16,
        right: 86,
        top: 16,
        zIndex: 3,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff34a",
          fontSize: 23,
          lineHeight: "24px",
          fontWeight: 900,
        }}
      >
        {title}
      </div>

      {subtitle && (
        <div
          className="shadow1"
          style={{
            color: "#fff",
            fontSize: 15,
            lineHeight: "17px",
            fontWeight: 800,
            marginTop: 4,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>

    <button
      type="button"
      className="game-button yellow-button"
      style={{
        position: "absolute",
        right: 8,
        bottom: 10,
        width: 78,
        height: 42,
        fontSize: 15,
        zIndex: 5,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {button}
    </button>
  </motion.div>
);

function Shop({ user, setUser, onNavigate, initialView = "hub" }) {
  const posthog = usePostHog();

  const [tonConnectUI] = useTonConnectUI();
  const tonWallet = useTonWallet();

  const normalizeView = (v) => {
    const s = String(v || "").toLowerCase();

    if (s === "hub" || s === "home" || s === "shop") return "hub";
    if (s === "cards" || s === "buycards" || s === "packs") return "cards";
    if (s === "coin_2" || s === "buyrunes" || s === "runes") return "coin_2";
    if (s === "coin_1" || s === "buysparks" || s === "sparks") return "coin_1";
    if (s === "items") return "items";

    return "hub";
  };

  const [view, setView] = useState(() =>
  normalizeView(initialView)
);

useEffect(() => {
  setView(normalizeView(initialView));
}, [initialView]);

const [selectedRunesIdx, setSelectedRunesIdx] = useState(null);
const [selectedSparksIdx, setSelectedSparksIdx] = useState(null);

const isAnyCheckedRunes = selectedRunesIdx !== null;
const isAnyCheckedSparks = selectedSparksIdx !== null;

const [showSuccessModal, setShowSuccessModal] = useState(false);
const [selectedPack, setSelectedPack] = useState(null);
const [showPackModal, setShowPackModal] = useState(false);
const [purchaseType, setPurchaseType] = useState(null);
const [purchasedCard, setPurchasedCard] = useState(null);
const [purchasedAmount, setPurchasedAmount] = useState(null);
const [showErrorModal, setShowErrorModal] = useState(false);
const [purchasedItem, setPurchasedItem] = useState(null);
const [errorMessage, setErrorMessage] = useState("");

const [tonPaymentBusy, setTonPaymentBusy] = useState(false);
const [tonPaymentStage, setTonPaymentStage] = useState("idle");

const [tonReceipt, setTonReceipt] = useState(null);

/*
 * Estas funções precisam ficar no escopo principal
 * do componente Shop, e não dentro de setShopView.
 */


const shortenTonValue = (
  value,
  start = 8,
  end = 6
) => {
  const text = String(value || "");

  if (text.length <= start + end + 3) {
    return text;
  }

  return `${text.slice(0, start)}...${text.slice(-end)}`;
};

const setShopView = (nextView) => {
  setView(nextView);

  requestAnimationFrame(() => {
    const shopEl =
      document.querySelector(".shop-container");

    if (shopEl) {
      shopEl.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }

    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  });
};

  const [shopItems, setShopItems] = useState([]);
  const [discountNow, setDiscountNow] = useState(Date.now());
  const [itemsLoading, setItemsLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const shopItemsLoadedRef = useRef(false);

  const [confirmItemOpen, setConfirmItemOpen] = useState(false);
  const [itemToBuy, setItemToBuy] = useState(null);

  const [itemBuyQuantity, setItemBuyQuantity] = useState(1);

  const [confirmCurrencyOpen, setConfirmCurrencyOpen] = useState(false);
  const [currencyToBuy, setCurrencyToBuy] = useState(null);

  const [showSideModal, setShowSideModal] = useState(false);
  const [sideTarget, setSideTarget] = useState(null);
  const [sideLoading, setSideLoading] = useState(false);
  const [sideUseBypass, setSideUseBypass] = useState(false);

  const [skinPreviewOpen, setSkinPreviewOpen] = useState(false);
  const [skinPreviewSeason, setSkinPreviewSeason] = useState(null);
  const [skinPreviewItemName, setSkinPreviewItemName] = useState(null);

  const [namePreviewOpen, setNamePreviewOpen] = useState(false);
  const [namePreviewItem, setNamePreviewItem] = useState(null);



  const { shop, arenas } = useAppConfig();

const changeSidePrice = shop?.changeSidePrice ?? 0;
const SIDE_CHANGE_BYPASS_RUNES_PRICE = 25;
// local state for "has gamepass" in the Shop
const [hasGamepass, setHasGamepass] = useState(false);

const chestLabel = (key) => {
  switch (key) {
    case "common": return "Common Chest";
    case "rare": return "Rare Chest";
    case "epic": return "Epic Chest";
    case "legendary": return "Legendary Chest";
    default: return "Chest";
  }
};

const chestIcon = (key) => {
  switch (key) {
    case "common": return "/common.png";
    case "rare": return "/rare.png";
    case "epic": return "/epic.png";
    case "legendary": return "/legendary.png";
    default: return "/pack.png";
  }
};

const getChestDisplayName = (item) => {
  const type = String(item?.chestType || item?.type || "").toLowerCase();

  if (type.includes("gold")) return "Lunar Horn Chest";
  if (type.includes("diamond")) return "Solar Horizon Chest";
  if (type.includes("silver")) return "Cursed Tomb Chest";
  if (type.includes("wood")) return "Dustbound Chest";

  return item?.name || "Chest";
};

const CHEST_META = {
  common:    { label: "Common Chest", image: "/chest1.png" },
  rare:      { label: "Rare Chest", image: "/chest2.png" },
  epic:      { label: "Epic Chest", image: "/chest3.png" },
  legendary: { label: "Legendary Chest", image: "/chest4.png" },

  wood:  { label: "Dustbound Chest", image: "shop_chest_5.png" },
  silver:  { label: "Cursed Tomb Chest", image: "shop_chest_3.png" },
  golden:  { label: "Lunar Horn Chest", image: "shop_chest_1.png" },
  diamond: { label: "Solar Horizon Chest", image: "shop_chest_2.png" },
};

const getChestMeta = (t) => CHEST_META[String(t || "").toLowerCase()] || null;

const CHEST_REWARD_META = {
  sparks: {
    label: "Sparks",
    icon: "/sparks.png",
  },

  runes: {
    label: "Runes",
    icon: "/runes.png",
  },

  common: {
    label: "Common Card",
    icon: "/common.png",
  },

  rare: {
    label: "Rare Card",
    icon: "/rare.png",
  },

  epic: {
    label: "Epic Card",
    icon: "/epic.png",
  },

  legendary: {
    label: "Legendary Card",
    icon: "/legendary.png",
  },

  abilities: {
  label: "Ability",
  icon: "/fightlog.png",
},

boosters: {
  label: "Booster",
  icon: "/energy.png",
},

skins: {
  label: "Skin",
  icon: "/fragment.png",
},

ticket: {
  label: "Ticket",
  icon: "/ticket.png",
},

cardPieces: {
  label: "Card Pieces",
  icon: "/pieces.png",
},

spins: {
  label: "Spins",
  icon: "/fortuneicon.png",
},

trophyShield: {
  label: "Trophy Shield",
  icon: "/shield.png",
},
};


const getChestRewardAmountText = (cfg) => {
  if (!cfg || typeof cfg !== "object") return "";

  if (cfg.amount != null) {
    return Number(cfg.amount).toLocaleString();
  }

  if (cfg.min != null && cfg.max != null) {
    const min = Number(cfg.min).toLocaleString();
    const max = Number(cfg.max).toLocaleString();

    return `${min} - ${max}`;
  }

  return "";
};


const getChestRewards = (item) => {
  if (String(item?.type || "").toLowerCase() !== "chest") {
    return [];
  }

  const rewards =
    item?._rewards ||
    item?.chest_rewards ||
    {};

  if (!rewards || typeof rewards !== "object") {
    return [];
  }

  return Object.entries(rewards)
    .map(([key, cfg]) => {
      const chance = Number(cfg?.chance || 0);

      if (chance <= 0) return null;

      const meta = CHEST_REWARD_META[key] || {
        label: titleCase(key),
        icon: null,
      };

      return {
        key,
        label: meta.label,
        icon: meta.icon,
        chance,
        amount: getChestRewardAmountText(cfg),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.chance - a.chance);
};

// Chests vindos do Config -> arenas[].chests
const chestShopItems = useMemo(() => {
  const arenaList = Array.isArray(arenas?.arenas)
    ? arenas.arenas
    : Array.isArray(arenas)
    ? arenas
    : [];

  const out = [];

  arenaList.forEach((a) => {
    const chestObj = a?.chests;
    if (!chestObj || typeof chestObj !== "object") return;

    Object.entries(chestObj).forEach(([chestType, chestCfg]) => {
      const priceRunes = Number(
  chestCfg?.priceRunes ??
  chestCfg?.price_runes ??
  chestCfg?.open_price_runes ??
  chestCfg?.runes ??
  0
);

const priceSparks = Number(
  chestCfg?.priceSparks ??
  chestCfg?.price_sparks ??
  chestCfg?.open_price_sparks ??
  chestCfg?.sparks ??
  0
);

// don't show if neither price exists
if (priceRunes <= 0 && priceSparks <= 0) return;

const meta = getChestMeta(chestType);

// RUNES version
if (priceRunes > 0) {
  out.push({
    id: `arena-chest:${a?.id}:${chestType}:runes`,
    type: "chest",
    chestType,
     _rewards: chestCfg?.rewards || {},
    name:
      meta?.label ||
      chestCfg?.name ||
      chestCfg?.display_name ||
      `${chestType.charAt(0).toUpperCase()}${chestType.slice(1)} Chest`,
    price_runes: priceRunes,
    price_sparks: 0,
    currency: "runes",
    _icon:
      meta?.image ||
      chestCfg?.icon ||
      chestCfg?.image ||
      `/${String(chestType).toLowerCase()}_chest.png`,
    _arenaId: a?.id,
  });
}

// SPARKS version
if (priceSparks > 0) {
  out.push({
    id: `arena-chest:${a?.id}:${chestType}:sparks`,
    type: "chest",
    chestType,
     _rewards: chestCfg?.rewards || {},
    name:
      meta?.label ||
      chestCfg?.name ||
      chestCfg?.display_name ||
      `${chestType.charAt(0).toUpperCase()}${chestType.slice(1)} Chest`,
    price_runes: 0,
    price_sparks: priceSparks,
    currency: "sparks",
    _icon:
      meta?.image ||
      chestCfg?.icon ||
      chestCfg?.image ||
      `/${String(chestType).toLowerCase()}_chest.png`,
    _arenaId: a?.id,
  });
}
    });
  });

  return out;
}, [arenas]);

const titleCase = (s) =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\w\S*/g, (t) => t[0].toUpperCase() + t.slice(1).toLowerCase());

const guessChestIcon = (chestType) => {
  const key = String(chestType || "").toLowerCase().trim();
  // tenta caminhos comuns (ajusta pro teu projeto)
  // se tu já tem assets tipo /gold_chest.png etc, aponta aqui
  return `/${key}_chest.png`;
};


const meetsSkinTrophyRequirement = (item) => {
  if (
    String(item?.type || "").toLowerCase() !==
    "seasonal_accessory"
  ) {
    return true;
  }

  const required = Number(
    item?.chest_rewards?.minTrophies ?? 0
  );

  if (!Number.isFinite(required) || required <= 0) {
    return true;
  }

  const trophies = Number(user?.trophies ?? 0);

  return trophies >= required;
};



const itemsForSale = useMemo(() => {
  return [
    ...chestShopItems,
    ...shopItems.filter((it) => {
      const type = String(it?.type || "").toLowerCase();

      return (
        type !== "discounts" &&
        meetsSkinTrophyRequirement(it)
      );
    }),
  ];
}, [chestShopItems, shopItems, user?.trophies]);


const activeDiscountOffers = useMemo(() => {
  const now = Date.now();

  const itemMap = new Map(
    itemsForSale.map((it) => [String(it.id), it])
  );

  const bestByItemId = new Map();

  shopItems.forEach((discountBundle) => {
    const type = String(discountBundle?.type || "").toLowerCase();

    if (type !== "discounts") return;
    if (discountBundle?.is_active === false) return;

    const expiresAt = discountBundle?.discount_expires_at;

    if (expiresAt) {
      const expiresMs = new Date(expiresAt).getTime();

      if (Number.isFinite(expiresMs) && expiresMs <= now) {
        return;
      }
    }

    const targets = Array.isArray(discountBundle?.discount_targets)
      ? discountBundle.discount_targets
      : [];

    targets.forEach((target) => {
      const itemId = String(target?.item_id || "");
      const percent = Number(target?.percent || 0);

      if (!itemId || percent <= 0) return;

      const targetItem = itemMap.get(itemId);
      if (!targetItem) return;

      const currentBest = bestByItemId.get(itemId);

      if (!currentBest || percent > currentBest.discountPercent) {
        bestByItemId.set(itemId, {
          item: targetItem,
          discountPercent: percent,
          discountBundleId: discountBundle.id,
          discountName: discountBundle.name,
          discountExpiresAt: discountBundle.discount_expires_at || null,
        });
      }
    });
  });

  return Array.from(bestByItemId.values());
}, [shopItems, itemsForSale]);

const nextDiscountExpiration = useMemo(() => {
  const expirations = activeDiscountOffers
    .map((offer) => offer.discountExpiresAt)
    .filter(Boolean)
    .map((date) => new Date(date).getTime())
    .filter((time) => Number.isFinite(time) && time > discountNow);

  if (!expirations.length) return null;

  return Math.min(...expirations);
}, [activeDiscountOffers, discountNow]);

const discountCountdownText = useMemo(() => {
  if (!nextDiscountExpiration) return "";

  const remainingMs = nextDiscountExpiration - discountNow;

  if (remainingMs <= 0) return "";

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
}, [nextDiscountExpiration, discountNow]);

useEffect(() => {
  if (!nextDiscountExpiration) return;

  const timer = setInterval(() => {
    setDiscountNow(Date.now());
  }, 1000);

  return () => clearInterval(timer);
}, [nextDiscountExpiration]);

const getDiscountForItem = (item) => {
  if (!item?.id) return null;

  return (
    activeDiscountOffers.find(
      (offer) => String(offer.item.id) === String(item.id)
    ) || null
  );
};

const getItemPriceInfo = (item) => {
  const discount = getDiscountForItem(item);

  const priceTelegramStars = Number(item.price_telegram_stars || 0);
  const priceSparks = Number(item.price_sparks || 0);
  const priceRunes = Number(item.price_runes || 0);

  const priceIsTelegramStars = priceTelegramStars > 0;
  const priceIsSparks = !priceIsTelegramStars && priceSparks > 0;

  const oldPrice = priceIsTelegramStars
    ? priceTelegramStars
    : priceIsSparks
      ? priceSparks
      : priceRunes;

  const discountPercent = Number(discount?.discountPercent || 0);

  const hasDiscount = discountPercent > 0 && oldPrice > 0;

  const finalPrice = hasDiscount
    ? Math.max(0, Math.ceil(oldPrice * (1 - discountPercent / 100)))
    : oldPrice;

  return {
    priceIsTelegramStars,
    priceIsSparks,
    oldPrice,
    finalPrice,
    hasDiscount,
    discountPercent,
    priceIcon: priceIsTelegramStars
      ? "/star.png"
      : priceIsSparks
        ? "/sparks.png"
        : "/runes.png",
    currencyLabel: priceIsTelegramStars
      ? "Telegram Stars"
      : priceIsSparks
        ? "Sparks"
        : "Runes",
  };
};

const confirmPriceInfo = itemToBuy
  ? getItemPriceInfo(itemToBuy)
  : {
      finalPrice: 0,
      oldPrice: 0,
      hasDiscount: false,
      currencyLabel: "",
    };

const confirmTotalPrice =
  Number(confirmPriceInfo.finalPrice || 0) *
  (confirmPriceInfo.priceIsTelegramStars
    ? 1
    : Number(itemBuyQuantity || 1));

const normalizeCardRarity = (rarity) => {
  const value = String(rarity || "")
    .trim()
    .toLowerCase();

  if (value === "legendary") return "legendary";
  if (value === "epic") return "epic";
  if (value === "rare") return "rare";

  return "common";
};

const getShopCardRarity = (item) => {
  const possibleValues = [
    item?.card_rarity,
    item?.cardRarity,
    item?.rarity,
    item?.card_type,
    item?.cardType,
    item?.reward_rarity,
    item?.rewardRarity,
    item?.metadata?.rarity,
    item?.meta?.rarity,
    item?.config?.rarity,
    item?.card?.rarity,
    item?.reward?.rarity,
    item?.details?.rarity,
  ];

  for (const possibleValue of possibleValues) {
    const value = String(possibleValue || "")
      .trim()
      .toLowerCase();

    if (value.includes("legendary")) return "legendary";
    if (value.includes("epic")) return "epic";
    if (value.includes("rare")) return "rare";
    if (value.includes("common")) return "common";
  }

  const searchableText = JSON.stringify(item || {}).toLowerCase();

  if (searchableText.includes("legendary")) return "legendary";
  if (searchableText.includes("epic")) return "epic";
  if (searchableText.includes("rare")) return "rare";
  if (searchableText.includes("common")) return "common";

  return "common";
};

const getShopCardIcon = (item) => {
  switch (getShopCardRarity(item)) {
    case "legendary":
      return "/legendary.png";

    case "epic":
      return "/epic.png";

    case "rare":
      return "/rare.png";

    case "common":
    default:
      return "/common.png";
  }
};

const getShopItemImageStyle = (it) => {
  const itemType = String(it?.type || "")
    .trim()
    .toLowerCase();

  if (itemType !== "name") {
    return {
      width: 42,
      height: 42,
      top: "calc(50% - 16px)",
    };
  }

  const category = getNameCategory(it);

  switch (category) {
    case "styles":
      return {
        width: 72,
        height: 72,
        top: "calc(50% - 31px)",
      };

    case "emogy":
      return {
        width: 46,
        height: 46,
        top: "calc(50% - 18px)",
      };

  case "banners":
  return {
    width: 80,
    height: 80,
    maxWidth: 80,
    maxHeight: 80,
    top: "calc(50% - 40px)",
  };

    default:
      return {
        width: 42,
        height: 42,
        top: "calc(50% - 16px)",
      };
  }
};

const getNameCategory = (it) => {
  const raw = String(it?.name_category || it?.nameCategory || "")
    .toLowerCase()
    .trim();

  if (raw === "styles" || raw === "style") return "styles";
  if (raw === "emogy" || raw === "emoji" || raw === "emojy") return "emogy";
  if (raw === "banners" || raw === "banner") return "banners";

  return "other";
};

const shopSections = useMemo(() => {
  const discountedItemIds = new Set(
    activeDiscountOffers.map((offer) => String(offer.item.id))
  );

  /*
   * NORMAL ITEMS
   *
   * Exclude:
   * - cosmetics
   * - skins / seasonal accessories
   * - chests
   * - discounts
   */
  const regularItems = itemsForSale.filter((it) => {
    const type = String(it?.type || "")
      .trim()
      .toLowerCase();

    return (
      type !== "name" &&
      type !== "seasonal_accessory" &&
      type !== "chest" &&
      type !== "discounts" &&
      !discountedItemIds.has(String(it.id))
    );
  });

  /*
   * SKINS
   *
   * Seasonal accessories are displayed separately.
   */
  const skins = itemsForSale.filter((it) => {
    const type = String(it?.type || "")
      .trim()
      .toLowerCase();

    return (
      type === "seasonal_accessory" &&
      !discountedItemIds.has(String(it.id))
    );
  });

  /*
   * CHESTS
   */
  const chests = itemsForSale.filter((it) => {
    const type = String(it?.type || "")
      .trim()
      .toLowerCase();

    return (
      type === "chest" &&
      !discountedItemIds.has(String(it.id))
    );
  });

  /*
   * NAME COSMETICS
   */
  const nameStyles = itemsForSale.filter((it) => {
    return (
      String(it?.type || "").toLowerCase() === "name" &&
      getNameCategory(it) === "styles" &&
      !discountedItemIds.has(String(it.id))
    );
  });

  const nameEmogy = itemsForSale.filter((it) => {
    return (
      String(it?.type || "").toLowerCase() === "name" &&
      getNameCategory(it) === "emogy" &&
      !discountedItemIds.has(String(it.id))
    );
  });

  const nameBanners = itemsForSale.filter((it) => {
    return (
      String(it?.type || "").toLowerCase() === "name" &&
      getNameCategory(it) === "banners" &&
      !discountedItemIds.has(String(it.id))
    );
  });

  return [
    {
      key: "skins",
      title: "Seasonal Skins",
      items: skins,
    },
    {
      key: "chests",
      title: "Treasure Chests",
      items: chests,
    },
     {
      key: "items",
      title: "Battle Essentials",
      items: regularItems,
    },
    {
      key: "name-styles",
      title: "Name Effects",
      items: nameStyles,
    },
    {
      key: "name-emogy",
      title: "Name Emblems",
      items: nameEmogy,
    },
    {
      key: "name-banners",
      title: "Profile Banners",
      items: nameBanners,
    },
  ];
}, [itemsForSale, activeDiscountOffers]);

useEffect(() => {
  let cancelled = false;

  // 1) first try to infer from the user object (if backend ever sends it there)
  const fromUser = userHasGamepass(user);
  if (fromUser) {
    setHasGamepass(true);
    return;
  }

  // 2) fallback: call /api/gamepass like GamepassPage does
  const token = localStorage.getItem("token");
  if (!token) return;

  (async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gamepass`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();
      if (cancelled) return;

      if (res.ok) {
        const value =
          !!(
            data.hasGamepass ??
            data.gamepassActive ??
            data.gamepass_active ??
            data.gamepass
          );

        setHasGamepass(value);
      } else {
        console.error("Gamepass check failed in Shop:", data?.error);
      }
    } catch (err) {
      if (!cancelled) {
        console.error("Gamepass check error in Shop:", err);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [user]);


const rawSide = user?.side || "good";
const currentSide = rawSide === "bad" || rawSide === "moon" ? "moon" : "sun";
const nextSide = currentSide === "sun" ? "moon" : "sun";

const SIDE_CHANGE_COOLDOWN_HOURS = 24;

const getSideCooldownRemainingMs = () => {
  const last = user?.last_side_change_at ?? user?.lastSideChangeAt;
  if (!last) return 0;

  const lastMs = new Date(last).getTime();
  if (!Number.isFinite(lastMs)) return 0;

  const cooldownMs = SIDE_CHANGE_COOLDOWN_HOURS * 60 * 60 * 1000;
  const remaining = cooldownMs - (Date.now() - lastMs);

  return remaining > 0 ? remaining : 0;
};

const sideCooldownRemainingMs = getSideCooldownRemainingMs();
const isSideCooldownActive = sideCooldownRemainingMs > 0;

const formatSideCooldown = (ms) => {
  const totalMinutes = Math.ceil(ms / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

const canAffordSideChange =
  changeSidePrice > 0 && (user?.sparks || 0) >= changeSidePrice;

const canAffordSideBypass =
  (user?.runes || 0) >= SIDE_CHANGE_BYPASS_RUNES_PRICE;

  const audioCtxRef = useRef(null);

  const ensureConfettiStyles = () => {
    if (document.getElementById("confetti-styles")) return;
    const css = `
    @keyframes confetti-fall {
      0%   { transform: translateY(-100vh) rotate(0deg);   opacity: 0; }
      8%   { opacity: 1; }
      100% { transform: translateY(100vh)  rotate(720deg); opacity: 0; }
    }
    .confetti-overlay {
      position: fixed; inset: 0; pointer-events: none; z-index: 9999; overflow: hidden;
      contain: layout paint; /* small perf win */
    }
    .confetti-piece {
      position: absolute; top: 0; left: 0;
      font-size: 16px; line-height: 1; text-shadow: 0 1px 0 #000;
      will-change: transform, opacity;
      /* Important: hidden & off-screen BEFORE animation kicks in */
      opacity: 0;
      transform: translateY(-120vh) rotate(0deg);
      animation-name: confetti-fall;
      animation-timing-function: ease-in;
      animation-fill-mode: both; /* keep last keyframe state until removal */
    }
  `;
    const style = document.createElement("style");
    style.id = "confetti-styles";
    style.textContent = css;
    document.head.appendChild(style);
  };

  const spawnConfettiBurst = (count = 80) => {
    ensureConfettiStyles();
    const overlay = document.createElement("div");
    overlay.className = "confetti-overlay";
    const symbols = ["✦", "★", "✧", "◆", "●", "■", "❤", "♦", "♢", "✪"];

    for (let i = 0; i < count; i++) {
      const el = document.createElement("span");
      el.className = "confetti-piece";
      el.textContent = symbols[Math.floor(Math.random() * symbols.length)];

      const delay = Math.random() * 0.25; // seconds
      const duration = 1.6 + Math.random() * 2.2; // seconds
      const hue = Math.floor(Math.random() * 360);
      const size = 14 + Math.random() * 10; // px

      // random horizontal start
      el.style.left = `${Math.random() * 100}vw`;
      // style details
      el.style.animationDuration = `${duration}s`;
      el.style.animationDelay = `${delay}s`;
      el.style.color = `hsl(${hue} 95% 60%)`;
      el.style.fontSize = `${size}px`;

      overlay.appendChild(el);
    }

    document.body.appendChild(overlay);
    setTimeout(() => {
      try {
        document.body.removeChild(overlay);
      } catch {}
    }, 4000);
  };

  const playChime = () => {
    try {
      const ctx =
        audioCtxRef.current ||
        new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;

      const now = ctx.currentTime;
      const notes = [
        { t: 0.0, f: 523.25, d: 0.16 }, // C5
        { t: 0.1, f: 659.25, d: 0.16 }, // E5
        { t: 0.2, f: 783.99, d: 0.18 }, // G5
        { t: 0.36, f: 1046.5, d: 0.22 }, // C6
      ];

      notes.forEach(({ t, f, d }, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = i === notes.length - 1 ? "triangle" : "sine";
        o.frequency.value = f;
        g.gain.value = 0;

        o.connect(g);
        g.connect(ctx.destination);

        const start = now + t;
        const end = start + d;
        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(0.28, start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, end);

        o.start(start);
        o.stop(end + 0.02);
      });
    } catch {}
  };

  const celebrate = (intensity = "normal") => {
    spawnConfettiBurst(intensity === "big" ? 120 : 80);
    playChime();
  };

  useEffect(() => {
    return () => {
      // cleanup audio on unmount
      try {
        audioCtxRef.current?.close();
      } catch {}
    };
  }, []);
  // --------------------------------------------------------------------------

  // Preload sprites
  useEffect(() => {
    [
      "/task_bg.png",
      "/bt_on.png",
      "/buy.png",
      "/runes.png",
      "/sparks.png",
      "/star.png",
      "/task_rect.png",
      "/checkedbox.png",
      "/uncheckedbox.png",
      "/coin_1.png",
      "/coin_2.png",
      "/pack.png",
      "/finger.png",
      "/ton.svg",
    ].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Build lists from CMS (safe defaults)
  const runeSkus = shop?.runes ?? []; // [{amount, price}]
  const sparkSkus = shop?.sparks ?? []; // [{amount, price}]
  const packs = useMemo(
    () => [
      {
        id: "common",
        title: "Common Pack",
        rarity: "Common",
        price: shop?.packs?.common ?? 0,
      },
      {
        id: "rare",
        title: "Rare Pack",
        rarity: "Rare",
        price: shop?.packs?.rare ?? 0,
      },
      {
        id: "epic",
        title: "Epic Pack",
        rarity: "Epic",
        price: shop?.packs?.epic ?? 0,
      },
      {
        id: "legendary",
        title: "Legendary Pack",
        rarity: "Legendary",
        price: shop?.packs?.legendary ?? 0,
      },
    ],
    [shop]
  );

  // --- Purchases -------------------------------------------------------------

  const goToInventoryItems = () => {
  setShowSuccessModal(false);
  setPurchaseType(null);
  setPurchasedAmount(null);
  setPurchasedCard(null);
  setPurchasedItem(null);
  setTonReceipt(null);
  onNavigate?.("inventoryHub", { tab: "items" });
};

  const purchaseRunes = async () => {
    const sku = runeSkus[selectedRunesIdx];
    if (!sku) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/purchase-currency`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            type: "sparks",
            amount: sku.amount,
            cost: sku.price, // send CMS "price" as "cost" to your current API
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        setErrorMessage(data?.error || "Purchase failed");
        setShowErrorModal(true);
        return;
      }

      setUser?.((prev) => ({
        ...prev,
        xp: data.user.xp,
        runes: data.user.runes,
        sparks: data.user.sparks ?? prev.sparks,
      }));

      setShowSuccessModal(true);
      setPurchaseType("sparks");
      setPurchasedAmount(sku.price);
      setSelectedRunesIdx(null);

      celebrate("normal");
    } catch (err) {
      console.error("Sparks purchase failed:", err);
      setErrorMessage("Network error");
      setShowErrorModal(true);
    }
  };

const changeSide = async ({ bypassCooldown = false } = {}) => {
  if (!sideTarget) return;
  setSideLoading(true);

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/shop/change-side`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          targetSide: sideTarget,
          bypassCooldown,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      setShowSideModal(false);
      setSideTarget(null);
      setSideUseBypass(false);
      setErrorMessage(data?.error || "Could not change side");
      setShowErrorModal(true);
      return;
    }

    setUser?.((prev) => ({
      ...prev,
      side: data.user.side,
      runes: data.user.runes ?? prev.runes,
      sparks: data.user.sparks ?? prev.sparks,
      last_side_change_at:
        data.user.last_side_change_at ??
        data.user.lastSideChangeAt ??
        prev.last_side_change_at ??
        prev.lastSideChangeAt,
      lastSideChangeAt:
        data.user.lastSideChangeAt ??
        data.user.last_side_change_at ??
        prev.lastSideChangeAt ??
        prev.last_side_change_at,
    }));

    setShowSideModal(false);
    setSideTarget(null);
    setSideUseBypass(false);

    setPurchaseType("side");
    setShowSuccessModal(true);
  } catch (err) {
    console.error("Change side failed:", err);

    setShowSideModal(false);
    setSideTarget(null);
    setSideUseBypass(false);

    setErrorMessage("Network error");
    setShowErrorModal(true);
  } finally {
    setSideLoading(false);
  }
};

  const purchaseSparks = async () => {
    const sku = sparkSkus[selectedSparksIdx];
    if (!sku) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/purchase-currency`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            type: "sparks",
            amount: sku.amount,
            cost: sku.price, // send CMS "price" as "cost" to your current API
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        setErrorMessage(data?.error || "Purchase failed");
        setShowErrorModal(true);
        return;
      }

      setUser?.((prev) => ({
        ...prev,
        xp: data.user.xp,
        runes: data.user.runes,
        sparks: data.user.sparks ?? prev.sparks,
      }));

      setShowSuccessModal(true);
      setPurchaseType("sparks");
      setPurchasedAmount(sku.amount);
      setSelectedSparksIdx(null);

      celebrate("normal");
    } catch (err) {
      console.error("Sparks purchase failed:", err);
      setErrorMessage("Network error");
      setShowErrorModal(true);
    }
  };

  //const isTMA = typeof window !== "undefined" && !!window.Telegram?.WebApp;
  const isTMA =
  typeof window !== "undefined" &&
  typeof window.Telegram?.WebApp?.initData === "string" &&
  window.Telegram.WebApp.initData.length > 0;

const purchaseRunesWithTon = async (purchaseData) => {
  if (tonPaymentBusy) return;

  const sku = purchaseData?.sku;

  if (!sku) {
    setErrorMessage("No Rune package selected.");
    setShowErrorModal(true);
    return;
  }

  /*
   * Quando ainda não existe carteira conectada,
   * abre somente o seletor de carteira.
   */
  if (!tonWallet) {
    try {
      await tonConnectUI.openModal();
    } catch (error) {
      console.error(
        "Could not open TON wallet selector:",
        error
      );
    }

    return;
  }

  const token = localStorage.getItem("token");

  if (!token) {
    setErrorMessage(
      "Authentication token is missing."
    );
    setShowErrorModal(true);
    return;
  }

  const runeAmount = Number(sku.amount);

  if (
    !Number.isFinite(runeAmount) ||
    runeAmount <= 0
  ) {
    setErrorMessage("Invalid Rune amount.");
    setShowErrorModal(true);
    return;
  }

  /*
   * Mantemos o modal normal de confirmação aberto.
   * O próprio modal passa a mostrar o estado de processamento.
   */
  setTonPaymentBusy(true);
setTonPaymentStage("wallet");
setTonReceipt(null);

  try {
    /*
     * 1. Cria a ordem no backend.
     */
    const orderRes = await fetch(
      `${process.env.REACT_APP_API_URL}/api/shop/ton/create-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: runeAmount,
        }),
      }
    );

    const order = await orderRes
      .json()
      .catch(() => ({}));

    if (!orderRes.ok || !order.ok) {
      throw new Error(
        order.error ||
          "Could not create the TON payment order."
      );
    }

    const amountNano = String(order.amountNano);
    const amountTon =
      Number(amountNano) / 1_000_000_000;

    const transaction = {
      validUntil: order.validUntil,
      network: order.network,
      messages: [
        {
          address: order.recipient,
          amount: amountNano,
          payload: order.payload,
        },
      ],
    };

    /*
     * 2. Abre o modal oficial da carteira.
     */
    const sentTransaction =
      await tonConnectUI.sendTransaction(
        transaction
      );

    if (!sentTransaction) {
      throw new Error(
        "The wallet did not return the transaction."
      );
    }

    setTonPaymentStage("blockchain");

    /*
     * 3. O modal normal continua aberto,
     * mostrando apenas "Confirming Payment".
     */
    const wait = (milliseconds) =>
      new Promise((resolve) =>
        setTimeout(resolve, milliseconds)
      );

    let paymentCredited = false;
    let creditedOrder = null;

    /*
     * Aguarda até 90 segundos.
     */
    for (
      let attempt = 0;
      attempt < 45;
      attempt += 1
    ) {
      await wait(2000);

      const statusRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/ton/order/${order.orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const statusData = await statusRes
        .json()
        .catch(() => ({}));

      if (!statusRes.ok) {
        continue;
      }

      const currentOrder =
        statusData?.order || {};

      const status =
        currentOrder.status ??
        statusData.status ??
        null;

      if (status === "credited") {
        paymentCredited = true;
        creditedOrder = currentOrder;
        break;
      }

      if (
        status === "failed" ||
        status === "expired"
      ) {
        throw new Error(
          status === "expired"
            ? "This TON payment order has expired."
            : "The TON payment could not be confirmed."
        );
      }
    }

    /*
     * Não chama isso de falha, pois o watcher
     * ainda pode confirmar depois.
     */
    if (!paymentCredited) {
      setConfirmCurrencyOpen(false);
      setCurrencyToBuy(null);

      setErrorMessage(
        "Your payment was sent and is still awaiting blockchain confirmation. The Runes will be added automatically after confirmation."
      );

      setShowErrorModal(true);
      return;
    }

    /*
     * 4. Atualiza o saldo real.
     */
    const meRes = await fetch(
      `${process.env.REACT_APP_API_URL}/api/me`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!meRes.ok) {
      throw new Error(
        "Payment was confirmed, but your balance could not be refreshed."
      );
    }

    const me = await meRes.json();

    setUser?.((previous) => ({
      ...previous,
      runes:
        me.runes ??
        previous?.runes,
      sparks:
        me.sparks ??
        previous?.sparks,
      xp:
        me.xp ??
        previous?.xp,
    }));

    setTonReceipt({
      status: "credited",
      orderId: order.orderId,
      runesAmount: runeAmount,
      amountNano,
      amountTon,
      sender:
        tonWallet?.account?.address || null,
      destination:
        order.recipient || null,
      explorerUrl:
        creditedOrder?.explorerUrl || null,
      transactionHash:
        creditedOrder?.transactionHash || null,
      newRuneBalance:
        me.runes ?? null,
    });

    /*
     * Fecha o modal normal e abre o sucesso.
     */
    setConfirmCurrencyOpen(false);
    setCurrencyToBuy(null);
    setSelectedSparksIdx(null);

    setPurchaseType("runes");
    setPurchasedAmount(runeAmount);
    setShowSuccessModal(true);

    celebrate("normal");
  } catch (error) {
    console.error(
      "TON Rune purchase failed:",
      error
    );

    const message = String(
      error?.message || ""
    );

    const normalizedMessage =
      message.toLowerCase();

    const wasCancelled =
      normalizedMessage.includes("reject") ||
      normalizedMessage.includes("cancel") ||
      normalizedMessage.includes("declined");

    /*
     * Se o usuário cancelou na carteira,
     * simplesmente volta ao modal normal.
     */
    if (wasCancelled) {
      return;
    }

    setConfirmCurrencyOpen(false);

    setErrorMessage(
      message || "TON payment failed."
    );

    setShowErrorModal(true);
  } finally {
    setTonPaymentBusy(false);
    setTonPaymentStage("idle");
  }
};

  const purchaseSparks_stars = async () => {
    const sku = sparkSkus[selectedSparksIdx];
    if (!sku) return;

    // If not inside Telegram, block (Stars only) or show message
    if (!isTMA || !window.Telegram.WebApp.openInvoice) {
      setErrorMessage(
        "Sparks can only be purchased with Telegram Stars inside the Telegram app."
      );
      setShowErrorModal(true);
      return;
    }

    try {
      // 1) create order & invoice on server
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/stars/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ amount: sku.amount, price: sku.price }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErrorMessage(
          data?.error || "Could not start Telegram Stars payment"
        );
        setShowErrorModal(true);
        return;
      }

      const { orderId, invoiceLink } = data;

      // 2) open invoice in Telegram Mini App
      window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
        // status: 'paid' | 'cancelled' | 'failed' (WebApp-side)
        if (status !== "paid") {
          setErrorMessage(
            status === "cancelled" ? "Payment cancelled" : "Payment failed"
          );
          setShowErrorModal(true);
          return;
        }

        // 3) optionally poll order status until webhook credits
        let tries = 0;
        const maxTries = 20;
        const wait = (ms) => new Promise((r) => setTimeout(r, ms));

        while (tries < maxTries) {
          const chk = await fetch(
            `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (chk.ok) {
            const j = await chk.json();
            if (j.status === "paid") break;
          }
          tries++;
          await wait(1500);
        }

        // 4) refresh balances from /api/me or trust local add (better: fetch /api/me)
        const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (meRes.ok) {
          const me = await meRes.json();
          setUser?.((prev) => ({
            ...prev,
            sparks: me.sparks,
            runes: me.runes,
            xp: me.xp,
          }));
        } else {
          // fallback optimistic
          setUser?.((prev) => ({
            ...prev,
            sparks: (prev.sparks || 0) + sku.amount,
          }));
        }

        setShowSuccessModal(true);
        setPurchaseType("runes");
        setPurchasedAmount(sku.amount);
        setSelectedSparksIdx(null);
        celebrate("normal");
      });
    } catch (err) {
      console.error("Stars payment error:", err);
      setErrorMessage("Network error");
      setShowErrorModal(true);
    }
  };

  const confirmPurchase = async () => {
    if (!selectedPack) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/purchase`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            rarity: selectedPack.rarity,
            price: selectedPack.price,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setShowSuccessModal(false);
        setShowPackModal(false);
        setErrorMessage(data?.error || "Purchase failed");
        setShowErrorModal(true);
        return;
      }

      setUser?.((prev) => ({
        ...prev,
        runes: data.user.runes,
        sparks: data.user.sparks ?? prev.sparks,
      }));

      setShowPackModal(false);
      setShowSuccessModal(true);
      setPurchaseType("card");
      setPurchasedCard(data.card);

      // PostHog tracking: character purchase
      posthog?.capture("character_purchased", {
        card_id: data.card?.id,
        card_name: data.card?.name,
        card_rarity: selectedPack.rarity,
        price: selectedPack.price,
        remaining_runes: data.user.runes,
        user_level: user?.level,
      });

      celebrate("big");
    } catch (err) {
      console.error("Purchase failed:", err);
      setErrorMessage("Network error");
      setShowErrorModal(true);
    }
  };

  const purchaseSelectedItemWithStars = async (item) => {
  if (!item?.id) return;

  if (!isTMA || !window.Telegram?.WebApp?.openInvoice) {
    setConfirmItemOpen(false);
    setItemToBuy(null);
    setSelectedItemId(null);

    setErrorMessage(
      "This item can only be purchased with Telegram Stars inside the Telegram app."
    );
    setShowErrorModal(true);
    return;
  }

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/shop/items/stars/create-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          itemId: item.id,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok || !data.ok) {
      setConfirmItemOpen(false);
      setItemToBuy(null);
      setSelectedItemId(null);

      setErrorMessage(data?.error || "Could not start Telegram Stars payment");
      setShowErrorModal(true);
      return;
    }

    const { orderId, invoiceLink } = data;

    window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
      if (status !== "paid") {
        setConfirmItemOpen(false);
        setItemToBuy(null);
        setSelectedItemId(null);

        setErrorMessage(
          status === "cancelled" ? "Payment cancelled" : "Payment failed"
        );
        setShowErrorModal(true);
        return;
      }

      let tries = 0;
      const maxTries = 20;
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));

      while (tries < maxTries) {
        const chk = await fetch(
          `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (chk.ok) {
          const j = await chk.json();
          if (j.status === "paid") break;
        }

        tries++;
        await wait(1500);
      }

      const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (meRes.ok) {
        const me = await meRes.json();

        setUser?.((prev) => ({
          ...prev,
          sparks: me.sparks ?? prev.sparks,
          runes: me.runes ?? prev.runes,
          tapi: me.tapi ?? prev.tapi,
          tickets: me.tickets ?? prev.tickets,
          xp: me.xp ?? prev.xp,
        }));
      }

      setPurchaseType("item");
      setPurchasedItem(item);
      setShowSuccessModal(true);
      setSelectedItemId(null);
      setConfirmItemOpen(false);
      setItemToBuy(null);

      celebrate("normal");
    });
  } catch (err) {
    console.error("Stars item purchase failed:", err);

    setConfirmItemOpen(false);
    setItemToBuy(null);
    setSelectedItemId(null);

    setErrorMessage("Network error");
    setShowErrorModal(true);
  }
};

 const purchaseSelectedItem = async (itemIdParam = null) => {
  const itemId = itemIdParam || itemToBuy?.id || selectedItemId;

  if (!itemId) return;

  if (String(itemId).startsWith("arena-chest:")) {
  const parts = String(itemId).split(":");

  const arenaId = parts[1];
  const chestKey = parts[2];
  const currency = parts[3];

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/purchase-arena-chest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
  arenaId,
  chestKey,
  currency,
  quantity: itemBuyQuantity,
}),
        }
      );

      const data = await res.json();

      if (!res.ok) {
  setConfirmItemOpen(false);
  setItemToBuy(null);
  setSelectedItemId(null);

  setErrorMessage(data?.error || "Purchase failed");
  setShowErrorModal(true);
  return;
}

      // update user runes / etc
      setUser?.((prev) => ({
  ...prev,
  runes: data.user.runes,
  sparks: data.user.sparks ?? prev.sparks,
  tapi: data.user?.tapi ?? prev.tapi,
  tickets: data.user?.tickets ?? prev.tickets,
}));

      setPurchaseType("item"); // ou "chest" se quiser separar
      setPurchasedItem({
        name: getChestDisplayName({
          chestType: chestKey,
          name: data?.chestName || "Chest",
        }),
      });
      setShowSuccessModal(true);
      setSelectedItemId(null);
      setConfirmItemOpen(false);
      setItemToBuy(null);
      celebrate("normal");
    } catch (err) {
      console.error("Chest purchase failed:", err);

  setConfirmItemOpen(false);
  setItemToBuy(null);
  setSelectedItemId(null);

  setErrorMessage("Network error");
  setShowErrorModal(true);
    }

    return;
  }

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/shop/purchase-item`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          itemId,
          quantity: itemBuyQuantity,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
  setConfirmItemOpen(false);
  setItemToBuy(null);
  setSelectedItemId(null);

  setErrorMessage(data?.error || "Purchase failed");
  setShowErrorModal(true);
  return;
}

    setUser?.((prev) => ({
      ...prev,
      runes: data.user.runes,
      sparks: data.user.sparks ?? prev.sparks,
      tapi: data.user?.tapi ?? prev.tapi,
      tickets: data.user?.tickets ?? prev.tickets,
    }));

    setPurchaseType("item");
    setPurchasedItem(data.item);
    setShowSuccessModal(true);
    setSelectedItemId(null);
    setConfirmItemOpen(false);
    setItemToBuy(null);

    celebrate("normal");
  } catch (err) {
      console.error("Item purchase failed:", err);

  setConfirmItemOpen(false);
  setItemToBuy(null);
  setSelectedItemId(null);

  setErrorMessage("Network error");
  setShowErrorModal(true);
  }
};

 useEffect(() => {
  if (view !== "hub" && view !== "items") return;

  if (shopItemsLoadedRef.current) {
    setItemsLoading(false);
    return;
  }

  const token = localStorage.getItem("token");

  if (!token) {
    setItemsLoading(false);
    return;
  }

  let cancelled = false;

  (async () => {
    try {
      setItemsLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/items`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (cancelled) return;

      if (!res.ok) {
        setErrorMessage(data?.error || "Could not load items");
        setShowErrorModal(true);
        return;
      }

      shopItemsLoadedRef.current = true;

      setShopItems(
        Array.isArray(data.items)
          ? data.items
          : []
      );
    } catch (e) {
      if (!cancelled) {
        setErrorMessage("Network error");
        setShowErrorModal(true);
      }
    } finally {
      if (!cancelled) {
        setItemsLoading(false);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [view]);

  const demoPreviewCard = useMemo(() => {
  const side = currentSide;

  const parts = [
    "background",
    "body",
    "boots",
    "eyes",
    "face",
    "head",
    "mouth",
    "neck",
    "pants",
    "shirt",
    "weapons",
  ];

  const character = {};

  parts.forEach((part) => {
    character[part] = {
      side,
      rarity: "Common",
      variant: 1,
    };
  });

  return {
    level: 1,
    rarity: "Common",
    attack: 3,
    defense: 2,
    health: 18,
    character,
  };
}, [currentSide]);

const renderNameStyleSample = (item, fontSize = 15) => {
  const styleValue = String(
    item?.name_value ?? item?.nameValue ?? ""
  ).trim();

  return (
    <div
        style={{
          maxWidth: "100%",
          fontSize: fontSize,
          lineHeight: 1.1,
          fontWeight: 800,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...getNameStyleCSS(styleValue),
        }}
      >
        TapTopia
      </div>
  );
};

const renderShopItemVisual = (it, imageStyle = null) => {
  const itemType = String(it?.type || "").trim().toLowerCase();
  const nameCategory =
    itemType === "name" ? getNameCategory(it) : null;

  if (itemType === "name" && nameCategory === "styles") {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 5px",
          boxSizing: "border-box",
        }}
      >
        {renderNameStyleSample(it, 15)}
      </div>
    );
  }

  const seasonalFragmentCount = Number(
  it?.accessory_total_items ??
  it?.accessoryTotalItems ??
  it?.total_fragments ??
  it?.totalFragments ??
  0
);

if (
  itemType === "seasonal_accessory" &&
  seasonalFragmentCount === 1
) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <div
  style={{
    transform: "translateY(8px)",
  }}
>
  {renderSkinPreviewThumb(
    demoPreviewCard,
    it?.season_name ?? it?.seasonName,
    76
  )}
</div>
    </div>
  );
}

  /*
   * NAME BANNERS:
   * Usa o div inteiro como área de alinhamento.
   * Mantém o tamanho natural da imagem.
   * Só reduz quando ultrapassa 90% do div.
   */
  if (itemType === "name" && nameCategory === "banners") {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={getShopItemIcon(it)}
          alt={it?.name || ""}
          draggable={false}
          onError={(e) => {
  console.error("SHOP ICON FAILED TO LOAD:", {
    item: it,
    attemptedSrc: e.currentTarget.src,
    returnedByFunction: getShopItemIcon(it),
  });

  e.currentTarget.onerror = null;
}}
          style={{
            display: "block",
            width: "auto",
            height: "auto",
            maxWidth: "80%",
            maxHeight: "80%",
            objectFit: "contain",
          }}
        />
      </div>
    );
  }

  /*
   * TODOS OS OUTROS ÍCONES:
   * Permanecem exatamente com o alinhamento original.
   */
  const resolvedStyle = imageStyle || getShopItemImageStyle(it);

  return (
   <img
  src={getShopItemIcon(it)}
  alt={it?.name || ""}
  draggable={false}
 onError={(e) => {
  console.error("SHOP IMAGE ERROR:", {
    itemName: it?.name,
    itemType: it?.type,
    attemptedImage: getShopItemIcon(it),
    fullItem: it,
  });

  e.currentTarget.onerror = null;
}}
  style={{
    position: "absolute",
    left: "50%",
    top: resolvedStyle.top,
    width: resolvedStyle.width,
    height: resolvedStyle.height,
    maxWidth: "90%",
    objectFit: "contain",
    transform: "translateX(-50%)",
  }}
/>
  );
};

const getNamePreviewImage = (it) => {
  const value = String(
    it?.name_value ??
    it?.nameValue ??
    it?.image ??
    it?.icon ??
    ""
  ).trim();

  if (!value) {
    const category = getNameCategory(it);

    if (category === "banners") return "/banner_icon.png";
    if (category === "emogy") return "/emogy_icon.png";

    return "/style_icon.png";
  }

  if (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `/name-cosmetics/${value}`;
};

const getShopItemIcon = (it) => {
  const type = String(
    it?.type ??
    it?.item_type ??
    it?.itemType ??
    ""
  )
    .trim()
    .toLowerCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");

  const name = String(
    it?.name ??
    it?.display_name ??
    it?.displayName ??
    ""
  ).toLowerCase();

  const fullItemText = JSON.stringify(it || {}).toLowerCase();

  // CARD PIECES precisa vir antes de CARD
  if (
    type === "card_pieces" ||
    type === "cardpieces" ||
    type === "card_piece" ||
    name.includes("card pieces") ||
    name.includes("card fragment")
  ) {
    return "/pieces.png";
  }

  /*
   * Reconhece o item como card pelo type, nome
   * ou por qualquer campo recebido do backend.
   */
  const isCard =
    type === "card" ||
    type === "cards" ||
    type === "character" ||
    type === "player_card" ||
    type.includes("_card") ||
    type.includes("card_") ||
    name.includes(" card") ||
    name.startsWith("card ") ||
    fullItemText.includes('"type":"card"') ||
    fullItemText.includes('"item_type":"card"');

  if (isCard) {
    if (fullItemText.includes("legendary")) {
      return "/legendary.png";
    }

    if (fullItemText.includes("epic")) {
      return "/epic.png";
    }

    if (fullItemText.includes("rare")) {
      return "/rare.png";
    }

    return "/common.png";
  }

  switch (type) {
    case "runes":
      return "/runes.png";

    case "sparks":
      return "/sparks.png";

      case "magic_potion":
  return "/magic_potion.png";

    case "booster":
      return "/energy.png";

    case "tapi":
      return "/icon_tapi.png";

    case "seasonal_accessory":
      return "/fragment.png";

    case "ticket":
      return "/ticket.png";

    case "ability":
      return "/fightlog.png";

    case "shield":
      return "/shield.png";

    case "spins":
      return "/fortuneicon.png";

    case "evolve":
      return "/evolveicon.png";

        case "name":
      return getNamePreviewImage(it);

    case "bundle": {
      const customImage =
        it?.image_url ||
        it?.imageUrl;

      return customImage || "/pack.png";
    }

    case "chest": {
  // Chest criado pelo Admin Items:
  // usa primeiro a imagem configurada no próprio item.
  const customImage =
    it?.image_url ||
    it?.imageUrl;

  if (customImage) {
    return customImage;
  }

  // Chest antigo vindo das Arenas:
  // continua usando o chestType / CHEST_META.
  const meta = getChestMeta(it?.chestType);

  if (meta?.image) {
    return meta.image.startsWith("/")
      ? meta.image
      : `/${meta.image}`;
  }

  if (it?._icon) {
    return it._icon;
  }

  return "/pack.png";
}

    default:
      return "/pack.png";
  }
};

const prettyItemType = (t) => {
  const s = String(t || "").toLowerCase();

  if (s === "chest") return "Chest";
  if (s === "magic_potion") return "Magic Potion";

  return String(t || "").replaceAll("_", " ");
};

const renderSkinPreviewThumb = (card, seasonName, size = 160) => {
  return (
    <div
      className="card-thumb"
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: 0,
        overflow: "visible",
        background: "transparent",
        border: 0,
        boxShadow: "none",
      }}
    >
      {Object.entries(card.character || {})
        .sort(([a], [b]) => {
          const order = [
  "background",
  "body",
  "pants",
  "shirt",
  "boots",
  "neck",
  "head",
  "face",
  "eyes",
  "mouth",
  "weapons",
];
          const indexA = order.includes(a) ? order.indexOf(a) : order.length;
          const indexB = order.includes(b) ? order.indexOf(b) : order.length;
          return indexA - indexB;
        })
        .map(([part, data]) => {
  if (part === "background") {
    return null;
  }

  const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;
  const src = getSkinSrc(seasonName, part, defaultSrc);

  return (
            <img
              key={part}
              src={src}
              alt={part}
              draggable={false}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                imageRendering: "pixelated",
                userSelect: "none",
                pointerEvents: "none",
              }}
              onError={(e) => {
                // fallback to default if skin sprite missing
                if (e.currentTarget.src !== defaultSrc) e.currentTarget.src = defaultSrc;
              }}
            />
          );
        })}
    </div>
  );
};

const itemHasConfirmPreview = (it) => {
  const itemType = String(it?.type || "").toLowerCase();
  return itemType === "seasonal_accessory" || itemType === "name";
};

const renderConfirmItemPreview = (it) => {
  const itemType = String(it?.type || "").toLowerCase();

  if (itemType === "seasonal_accessory") {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginBottom: -10 }}>
        {renderSkinPreviewThumb(demoPreviewCard, it.season_name)}
      </div>
    );
  }

  if (itemType === "name") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 90,
          marginBottom: 10,
        }}
      >
        {getNameCategory(it) === "styles" ? (
          renderNameStyleSample(it, 48)
        ) : (
          <img
            src={getNamePreviewImage(it)}
            alt={it.name}
            style={{
  width: getNameCategory(it) === "banners" ? "90%" : "auto",
  maxWidth: getNameCategory(it) === "banners" ? "280px" : "170px",
  height: "auto",
  maxHeight: getNameCategory(it) === "banners" ? "100px" : "75px",
  objectFit: "contain",
  filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.6))",
}}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/pack.png";
            }}
          />
        )}
      </div>
    );
  }

  return (
    <img
      src={getShopItemIcon(it)}
      alt={it.name}
      style={{
        height: 80,
      }}
    />
  );
};

  if ((view === "hub" || view === "items") && itemsLoading) {
  return (
    <div
      className="shop-container"
      style={{
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      <LoadingSpinner text="Loading Shop" size={64} />
    </div>
  );
}

return (
  <div className="shop-container" style={{ paddingBottom: 0 }}>
    <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    width: "100%",
    gap: 6,
    alignItems: "stretch",
    marginTop: -8,
    marginBottom: 12,
  }}
>
  {[
    { key: "hub", label: "SHOP" },
    { key: "cards", label: "CARDS" },
    { key: "items", label: "ITEMS" },
    { key: "coin_1", label: "SPARKS" },
    { key: "coin_2", label: "RUNES" },
  ].map((t) => {
    const isActive = view === t.key;

    return (
      <button
        key={t.key}
        type="button"
        onClick={() => setShopView(t.key)}
        style={{
          width: "100%",
          minWidth: 0,
          height: 46,
          padding: "0 3px",
          margin: 0,
          boxSizing: "border-box",

          background: isActive
            ? "rgba(112, 61, 27, 0.96)"
            : "rgba(95, 49, 18, 0.76)",

          border: isActive
            ? "2px solid rgba(135, 78, 36, 0.98)"
            : "2px solid rgba(70, 32, 10, 0.95)",

          borderRadius: 7,

          boxShadow: isActive
            ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
            : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

          color: "#fff",
          fontSize: 12,
          lineHeight: "42px",
          fontWeight: 600,
          textAlign: "center",
          textTransform: "uppercase",

          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",

          cursor: "pointer",
          appearance: "none",
          WebkitAppearance: "none",
          opacity: isActive ? 1 : 0.88,
          fontFamily: '"Fredoka", sans-serif',
        }}
      >
        {t.label}
      </button>
    );
  })}
</div>

{/* SHOP HUB */}
{view === "hub" && !itemsLoading && (
  <div style={{marginLeft: -10, marginRight: -10, marginTop: 0}}>
   {activeDiscountOffers.length > 0 && (
      <ShopHubTitle>This Week's Special Offers</ShopHubTitle>
    )}

<div>
 {activeDiscountOffers.length === 0 ? null : (
    <div
    className="modal_body modal_body_2"
      style={{
        marginBottom: 0,
        display: "grid",
        paddingBottom: 5,
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 10,
        boxSizing: "border-box",
      }}
    >
      {activeDiscountOffers.slice(0, 6).map((offer) => {
        const it = offer.item;
        const priceInfo = getItemPriceInfo(it);

        return (
          <button
            key={`hub-discount-offer-${offer.discountBundleId}-${it.id}`}
            type="button"
            onClick={() => {
              setItemToBuy(it);
              setItemBuyQuantity(1);
              setConfirmItemOpen(true);
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              position: "relative",
              overflow: "hidden",
              cursor: "pointer",
              borderRadius: 5,
              backgroundImage: 'url("/v2/item.png")',
              backgroundRepeat: "repeat",
              backgroundSize: "100% 100%",
              backgroundPosition: "center center",
              border: 0,
              padding: 0,
            }}
          >
            <div
              className="shadow1"
              style={{
                backgroundImage: 'url("v2/discount.png")',
                backgroundRepeat: "repeat",
                backgroundSize: "100%",
                backgroundPosition: "center",
                position: "absolute",
                width: 42,
                height: 24,
                top: 10,
                right: 3,
                zIndex: 5,
                color: "#fff",
                fontSize: 12,
                lineHeight: "22px",
                paddingLeft: 7,
                boxSizing: "border-box",
                fontWeight: 600

              }}
            >
              -{offer.discountPercent}%
            </div>
            
            <div style={{float: "left",width: "100%", display: "block", position: "relative", aspectRatio: 1.3}}>
              {renderShopItemVisual(it)}
            </div>

            <div
              className="shadow1"
              title={it.name}
              style={{
                position: "relative",
                display: "block",
                float: "left",
                width: "calc(100% - 6px)",
                paddingLeft: 5,
                paddingRight: 5,
                boxSizing: "border-box",
                margin: 3,
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                textAlign: "center",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                background: "#341a0d",
                marginBottom: 0,
                lineHeight: "24px"
              }}
            >
              {it.name}
            </div>

            <div
              style={{
                float: "left",
                width: "calc(100% - 6px)",
                boxSizing: "border-box",
                margin: 3,
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
                background: "#7e4123",
                lineHeight: "24px",
                marginTop: 0
              }}
            >
              {priceInfo.hasDiscount && (
                <span
                  className="shadow1"
                  style={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: 15,
                    textDecoration: "line-through",
                  }}
                >
                  {priceInfo.oldPrice}
                </span>
              )}

              <img
                src={priceInfo.priceIcon}
                alt=""
                draggable={false}
                style={{
                  width: 22,
                  height: 22,
                  objectFit: "contain",
                }}
              />

              <span className="shadow1">{priceInfo.finalPrice}</span>
            </div>
          </button>
        );
      })}
    </div>
  )}

  {discountCountdownText && (
    <div
      style={{
        width: "84%",
        height: 42,
        margin: "0 auto 0",
        marginBottom: 15,
        boxSizing: "border-box",
        border: "1px solid #4c210b",
        borderTop: 0,
        borderRadius: "0 0 10px 10px",
        background: "rgba(113, 70, 25, 0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 4px 0 10px",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#ffe24a",
          fontSize: 16,
          fontWeight: 600,
        }}
      >
        OFFERS ENDS IN:
      </div>

      <div
        className="shadow1"
        style={{
          minWidth: 112,
          height: 32,
          padding: "0 10px",
          borderRadius: 7,
          background: "#4b160e",
          color: "#fff",
          fontSize: 16,
          lineHeight: "32px",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        <img style={{verticalAlign: "middle", height: 25, marginTop: -3}} src="/v2/clock2.png"/> {discountCountdownText}
      </div>
    </div>
  )}
</div>

    <ShopHubTitle4>Get OG PASS</ShopHubTitle4>

    <div
  onClick={() => onNavigate?.("gamepass")}
  style={{
    position: "relative",
    margin: 15,
    marginTop: -5,
    width: "calc(100% - 30px)",
    cursor: "pointer",
  }}
>
  <img
    src="/ogpassnormal.png"
    style={{
      aspectRatio: 361 / 144,
      width: "100%",
    }}
  />
</div>

<div>
    <ShopHubTitle4>Get OG PASS PLUS</ShopHubTitle4>
    <div
  onClick={() => onNavigate?.("battlePass")}
  style={{
    position: "relative",
    margin: 15,
    marginTop: -5,
    width: "calc(100% - 30px)",
    cursor: "pointer",
  }}
>
  <img
    src="/ogpassplus.png"
    style={{
      aspectRatio: 361 / 144,
      width: "100%",
    }}
  />
</div></div>

    <ShopHubTitle3>Items Store</ShopHubTitle3>

    <div onClick={() => setShopView("items")} style={{position: "relative", margin: 15, marginTop: -5, width: "calc(100% - 30px)", cursor: "pointer"}}>
    <img src="/sb_3.png" style={{aspectRatio: 361 / 144,width: "100%"}}/>

    <div className="shadow1" style={{fontSize: 16, fontWeight: 600, color: "#fff", position: "absolute", left: 15,top: 12}}>Power Up Your Game</div>
    <div className="shadow1" style={{fontSize: 12, textAlign: "left", width: 90, fontWeight: 600, color: "#fff", position: "absolute", left: 15,top: 33}}>Get skins, boosts, chests, and spins.</div>
    <div className="game-button-small yellow-button" style={{position: "absolute", left: 15, bottom: 20}}>ENTER</div>

    </div>

    <ShopHubTitle3>Special Players Cards</ShopHubTitle3>
    <div onClick={() => setShopView("cards")} style={{position: "relative", margin: 15, marginTop: -5, width: "calc(100% - 30px)"}}>
    <img src="/v2/shop_cards.png" style={{aspectRatio: 361 / 156,width: "100%"}}/>

    <div className="shadow1" style={{fontSize: 21, fontWeight: 600, color: "#f3e14d", position: "absolute", left: 15,top: 12}}>Recruit. Upgrade. Conquer.</div>
    <div className="shadow1" style={{fontSize: 16, fontWeight: 600, color: "#fff", position: "absolute", left: 15,top: 36}}>Collect rare fighters. Build your squad.</div>
    <div className="game-button-small yellow-button" style={{position: "absolute", right: 15, bottom: 20}}>ENTER</div>

    </div>

    <ShopHubTitle3>MeerkatPlace</ShopHubTitle3>

     <div
  onClick={() => onNavigate?.("marketplace")}
  style={{
    position: "relative",
    margin: 15,
    marginTop: -5,
    width: "calc(100% - 30px)",
    cursor: "pointer",
  }}
>
  <img
    src="/v2/shop_meerkat.png"
    style={{
      aspectRatio: 361 / 156,
      width: "100%",
    }}
  />

  <div
    className="shadow1"
    style={{
      fontSize: 21,
      fontWeight: 600,
      color: "#f3e14d",
      position: "absolute",
      right: 15,
      top: 12,
    }}
  >
    Trade Smarter
  </div>

  <div
    className="shadow1"
    style={{
      fontSize: 16,
      fontWeight: 600,
      color: "#fff",
      position: "absolute",
      right: 15,
      top: 36,
    }}
  >
    Find deals and grow your squad.
  </div>

  <div
    className="game-button-small yellow-button"
    style={{
      position: "absolute",
      right: 15,
      bottom: 20,
    }}
  >
    ENTER
  </div>
</div>

    <ShopHubTitle5>Buy Runes</ShopHubTitle5>

     <div onClick={() => setShopView("coin_2")} style={{position: "relative", margin: 15, marginTop: -5, width: "calc(100% - 30px)"}}>
    <img src="/v2/shop_runes.png" style={{aspectRatio: 361 / 156,width: "100%"}}/>

    <div className="shadow1" style={{fontSize: 18, fontWeight: 600, color: "#f3e14d", position: "absolute", left: 15,top: 13}}>Power Without Limits</div>
    <div className="shadow1" style={{width: "40%", fontSize: 12, fontWeight: 600, color: "#fff", position: "absolute", left: 15,top: 37}}>Unlock exclusive content with Runes.</div>
    <div className="game-button-small yellow-button" style={{position: "absolute", right: 15, bottom: 20}}>OPEN SHOP</div>

    <div
      className="shadow1"
      style={{
        backgroundImage: 'url("/v2/discount2.png")',
        backgroundRepeat: "repeat",
        backgroundSize: "100%",
        backgroundPosition: "center",
        position: "absolute",
        width: 67,
        height: 53,
        top: 15,
        right: 2,
        zIndex: 5,
        color: "#fff",
        fontSize: 18,
        lineHeight: "50px",
        paddingLeft: 10,
        boxSizing: "border-box",
        fontWeight: 600,
      }}
    >
      50%
    </div>

    </div>

    <ShopHubTitle5>Buy Sparks</ShopHubTitle5>

     <div onClick={() => setShopView("coin_1")} style={{position: "relative", margin: 15, marginTop: -5, width: "calc(100% - 30px)"}}>
    <img src="/v2/shop_sparks.png" style={{aspectRatio: 361 / 156,width: "100%"}}/>

    <div className="shadow1" style={{fontSize: 18, fontWeight: 600, color: "#f3e14d", position: "absolute", left: 15,top: 16}}>Fuel Your Upgrades</div>
    <div className="shadow1" style={{fontSize: 12, fontWeight: 600, color: "#fff", position: "absolute", left: 15,top: 40}}>Get Sparks to level up faster.</div>
    <div className="game-button-small yellow-button" style={{position: "absolute", right: 15, bottom: 20}}>OPEN SHOP</div>

    <div
      className="shadow1"
      style={{
        display: "none",
        backgroundImage: 'url("/v2/discount2.png")',
        backgroundRepeat: "repeat",
        backgroundSize: "100%",
        backgroundPosition: "center",
        position: "absolute",
        width: 67,
        height: 53,
        top: 15,
        right: 2,
        zIndex: 5,
        color: "#fff",
        fontSize: 18,
        lineHeight: "50px",
        paddingLeft: 10,
        boxSizing: "border-box",
        fontWeight: 600,
      }}
    >
      25%
    </div>

    </div>

    
  </div>
)}

     {/* CARDS */}
{view === "cards" && (
  <div style={{ float: "left", marginLeft: -10, marginRight: -10, marginTop: -5 }}>

  <ShopHubTitle3>MeerkatPlace</ShopHubTitle3>

     <div
  onClick={() => onNavigate?.("marketplace")}
  style={{
    position: "relative",
    margin: 15,
    marginTop: -5,
    width: "calc(100% - 30px)",
    cursor: "pointer",
  }}
>
  <img
    src="/v2/shop_meerkat.png"
    style={{
      aspectRatio: 361 / 156,
      width: "100%",
    }}
  />

  <div
    className="shadow1"
    style={{
      fontSize: 21,
      fontWeight: 600,
      color: "#f3e14d",
      position: "absolute",
      right: 15,
      top: 12,
    }}
  >
    Trade Smarter
  </div>

  <div
    className="shadow1"
    style={{
      fontSize: 16,
      fontWeight: 600,
      color: "#fff",
      position: "absolute",
      right: 15,
      top: 36,
    }}
  >
    Find deals and grow your squad.
  </div>

  <div
    className="game-button-small yellow-button"
    style={{
      position: "absolute",
      right: 15,
      bottom: 20,
    }}
  >
    ENTER
  </div>
</div>


    <ShopHubTitle2>Special Players Cards</ShopHubTitle2>

    <div
      className="modal_body modal_body_2"
      style={{
        display: "grid",
        paddingBottom: 5,
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 10,
        boxSizing: "border-box",
      }}
    >
      {packs.map((pack) => (
        <button
          key={pack.id}
          type="button"
          onClick={() => {
            setSelectedPack({
              rarity: pack.rarity,
              price: pack.price,
            });
            setShowPackModal(true);
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            borderRadius: 10,
            backgroundImage: 'url("/v2/item.png")',
            backgroundRepeat: "repeat",
            backgroundSize: "100% 100%",
            backgroundPosition: "center center",
            border: 0,
            padding: 0,
          }}
        >
          <div
            style={{
              float: "left",
              position: "relative",
              aspectRatio: 1.3,
              width: "100%",
            }}
          >
            <img
              src={`/${pack.id}.png`}
              alt={pack.title}
              draggable={false}
              style={{
                position: "absolute",
                left: "calc(50% - 45px)",
                top: "calc(50% - 38px)",
                width: 90,
                height: 90,
                objectFit: "contain",
              }}
            />
          </div>

          <div
            className="shadow1"
            title={pack.title}
            style={{
              float: "left",
              paddingLeft: 5,
              paddingRight: 5,
              margin: 5,
              width: "calc(100% - 10px)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              textAlign: "center",
              overflow: "hidden",
              boxSizing: "border-box",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              background: "#341a0d",
              marginBottom: 0,
              lineHeight: "24px",
            }}
          >
            {pack.title}
          </div>

          <div
            style={{
              float: "left",
              margin: 5,
              width: "calc(100% - 10px)",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              background: "#7e4123",
              lineHeight: "24px",
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            <img
              src="/runes.png"
              alt=""
              draggable={false}
              style={{
                width: 21,
                height: 21,
                objectFit: "contain",
              }}
            />

            <span className="shadow1">{pack.price}</span>
          </div>
        </button>
      ))}
    </div>
  </div>
)}

    {/* ITEMS */}
{view === "items" && (
  <div>
    <>
        {/* DISCOUNT SECTION */}
<div style={{marginLeft: -10, marginRight: -10, marginTop: 10 }}>
  {activeDiscountOffers.length > 0 && (
  <ShopHubTitle>This Week's Special Offers</ShopHubTitle>
)}

  <div>
    {activeDiscountOffers.length === 0 ? null : (
      <div
        className="modal_body modal_body_2"
        style={{
          marginBottom: 0,
          display: "grid",
          paddingBottom: 5,
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
          boxSizing: "border-box",
        }}
      >
        {activeDiscountOffers.slice(0, 6).map((offer) => {
          const it = offer.item;
          const priceInfo = getItemPriceInfo(it);

          return (
            <button
              key={`items-discount-offer-${offer.discountBundleId}-${it.id}`}
              type="button"
              onClick={() => {
                setItemToBuy(it);
                setItemBuyQuantity(1);
                setConfirmItemOpen(true);
              }}
              style={{
                width: "100%",
                boxSizing: "border-box",
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                borderRadius: 10,
                backgroundImage: 'url("/v2/item.png")',
                backgroundRepeat: "repeat",
                backgroundSize: "100% 100%",
                backgroundPosition: "center center",
                border: 0,
                padding: 0,
              }}
            >
              <div
                className="shadow1"
                style={{
                  backgroundImage: 'url("/v2/discount.png")',
                  backgroundRepeat: "repeat",
                  backgroundSize: "100%",
                  backgroundPosition: "center",
                  position: "absolute",
                  width: 42,
                  height: 24,
                  top: 10,
                  right: 3,
                  zIndex: 5,
                  color: "#fff",
                  fontSize: 12,
                  lineHeight: "22px",
                  paddingLeft: 7,
                  boxSizing: "border-box",
                  fontWeight: 600,
                }}
              >
                -{offer.discountPercent}%
              </div>

              <div style={{float: "left",position: "relative", aspectRatio: 1.3, width: "100%"}}>
                {renderShopItemVisual(it)}
              </div>

              <div
                className="shadow1"
                title={it.name}
                style={{
                  float: "left",
                  margin: 3,
                  paddingLeft: 5,
                  paddingRight: 5,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "center",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  background: "#341a0d",
                  marginBottom: 0,
                  boxSizing: "border-box",
                  width: "calc(100% - 6px)",
                  lineHeight: "24px"
                }}
              >
                {it.name}
              </div>

              <div
                style={{
                  float: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  background: "#7e4123",
                  lineHeight: "24px",
                  margin: 3,
                  width: "calc(100% - 6px)",
                  marginBottom: 6
                }}
              >
                {priceInfo.hasDiscount && (
                  <span
                    className="shadow1"
                    style={{
                      color: "rgba(255,255,255,0.72)",
                      fontSize: 15,
                      textDecoration: "line-through",
                    }}
                  >
                    {priceInfo.oldPrice}
                  </span>
                )}

                <img
                  src={priceInfo.priceIcon}
                  alt=""
                  draggable={false}
                  style={{
                    width: 22,
                    height: 22,
                    objectFit: "contain",
                  }}
                />

                <span className="shadow1">{priceInfo.finalPrice}</span>
              </div>
            </button>
          );
        })}
      </div>
    )}

    {discountCountdownText && (
      <div
        style={{
          width: "84%",
          height: 42,
          margin: "0 auto 0",
          marginBottom: 15,
          boxSizing: "border-box",
          border: "1px solid #4c210b",
          borderTop: 0,
          borderRadius: "0 0 10px 10px",
          background: "rgba(113, 70, 25, 0.82)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 4px 0 10px",
        }}
      >
        <div
          className="shadow1"
          style={{
            color: "#ffe24a",
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          OFFERS ENDS IN:
        </div>

        <div
          className="shadow1"
          style={{
            minWidth: 112,
            height: 32,
            padding: "0 10px",
            borderRadius: 7,
            background: "#4b160e",
            color: "#fff",
            fontSize: 16,
            lineHeight: "32px",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          <img
            style={{
              verticalAlign: "middle",
              height: 25,
              marginTop: -3,
            }}
            src="/v2/clock2.png"
            alt=""
          />{" "}
          {discountCountdownText}
        </div>
      </div>
    )}
  </div>
</div>
        {itemsForSale.length === 0 && (
          <div className="text-center text-muted py-4">
            No items for sale right now.
          </div>
        )}

        {shopSections
  .filter((section) => section.key !== "discounts")
  .map((section, sectionIndex) => {
    if (!section.items.length) return null;

    return (
      <div
  key={section.key}
  style={{
    float: "left",
    width: "calc(100% + 20px)",
    marginLeft: -10,
    marginRight: -10,
    marginTop: sectionIndex === 0 ? -5 : 2,
    marginBottom: 6,
    clear: "both",
  }}
>
        <ShopHubTitle2>{section.title}</ShopHubTitle2>

        <div
          className="modal_body modal_body_2"
          style={{
             display: "grid",
  paddingBottom: 10,
  marginBottom: 0,
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 10,
            boxSizing: "border-box",
          }}
        >
         {section.items.map((it) => {
  const itemType = String(it?.type || "").toLowerCase();

  const priceInfo = getItemPriceInfo(it);
  const priceIcon = priceInfo.priceIcon;
  const priceValue = priceInfo.finalPrice;

  const imageStyle = getShopItemImageStyle(it);

            return (
              <button
                key={`item-${it.id}`}
                type="button"
                onClick={() => {
                  setItemToBuy(it);
                  setItemBuyQuantity(1);
                  setConfirmItemOpen(true);
                }}
                style={{
  width: "100%",
  boxSizing: "border-box",
  position: "relative",
  overflow: "hidden",
  cursor: "pointer",
  borderRadius: 10,
  backgroundImage: 'url("/v2/item.png")',
  backgroundRepeat: "repeat",
  backgroundSize: "100% 100%",
  backgroundPosition: "center center",
  border: 0,
  padding: 0,

  display: "flex",
  flexDirection: "column",
  minHeight: 128,
}}
              >
                {priceInfo.hasDiscount && (
                  <div
                    className="shadow1"
                    style={{
                      backgroundImage: 'url("/v2/discount.png")',
                      backgroundRepeat: "repeat",
                      backgroundSize: "100%",
                      backgroundPosition: "center",
                      position: "absolute",
                      width: 42,
                      height: 24,
                      top: 10,
                      right: 3,
                      zIndex: 5,
                      color: "#fff",
                      fontSize: 12,
                      lineHeight: "22px",
                      paddingLeft: 7,
                      boxSizing: "border-box",
                      fontWeight: 600,
                    }}
                  >
                    -{priceInfo.discountPercent}%
                  </div>
                )}

                <div style={{position: "relative", aspectRatio: 1.3, width: "100%", flexShrink: 0}}>
                  {renderShopItemVisual(it, imageStyle)}
                </div>

                <div
                  className="shadow1"
                  title={it.name}
                  style={{
                    paddingLeft: 5,
                    paddingRight: 5,
                    margin: 3,
                    width: "calc(100% - 6px)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "center",
                    overflow: "hidden",
                    boxSizing: "border-box",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    background: "#341a0d",
                    marginBottom: 0,
                    lineHeight: "24px"
                  }}
                >
                  {it.name}
                </div>

                <div
                  style={{
                    margin: 3,
                    width: "calc(100% - 6px)",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                    background: "#7e4123",
                    lineHeight: "24px",
                    marginTop: 0,
                    marginBottom: 6
                  }}
                >
                  {priceInfo.hasDiscount && (
                    <span
                      className="shadow1"
                      style={{
                        color: "rgba(255,255,255,0.72)",
                        fontSize: 15,
                        textDecoration: "line-through",
                      }}
                    >
                      {priceInfo.oldPrice}
                    </span>
                  )}

                  <img
                    src={priceIcon}
                    alt=""
                    draggable={false}
                    style={{
                      width: 21,
                      height: 21,
                      objectFit: "contain",
                    }}
                  />

                  <span className="shadow1">{priceValue}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  })}
    <div style={{ float: "left", clear: "both", width: "100%", height: 10 }} />
    </>
  </div>
)}

      {/* SPARKS */}
{view === "coin_1" && (
  <div style={{ float: "left", marginLeft: -10, marginRight: -10, marginTop: -5 }}>
    <ShopHubTitle2>Buy Sparks</ShopHubTitle2>

    {runeSkus.length === 0 && (
      <div className="text-center text-muted py-4" style={{ color: "#fff" }}>
        No spark packs configured.
      </div>
    )}

    {runeSkus.length > 0 && (
      <div
        className="modal_body modal_body_2"
        style={{
          display: "grid",
          paddingBottom: 5,
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
          boxSizing: "border-box",
        }}
      >
        {runeSkus.map((sku, idx) => {
          const isChecked = selectedRunesIdx === idx;

          return (
            <button
              key={`sparks-${idx}`}
              type="button"
              onClick={() => {
  setSelectedRunesIdx(idx);
  setCurrencyToBuy({
  type: "sparks",
  idx,
  sku,
  title: "Sparks",

  // What the user RECEIVES
  amount: sku.price,

  // What the user PAYS
  price: sku.amount,

  itemIcon: "/sparks.png",
  priceIcon: "/runes.png",
  priceLabel: "Runes",
});
  setConfirmCurrencyOpen(true);
}}
              style={{
                width: "100%",
                boxSizing: "border-box",
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                borderRadius: 10,
                backgroundImage: 'url("/v2/item.png")',
                backgroundRepeat: "repeat",
                backgroundSize: "100% 100%",
                backgroundPosition: "center center",
                border: 0,
                padding: 0,
              }}
            >
              {isChecked && (
                <div
                  className="shadow1"
                  style={{
                    backgroundImage: 'url("/v2/discount.png")',
                    backgroundRepeat: "repeat",
                    backgroundSize: "100%",
                    backgroundPosition: "center",
                    position: "absolute",
                    width: 42,
                    height: 24,
                    top: 10,
                    right: 3,
                    zIndex: 5,
                    color: "#fff",
                    fontSize: 12,
                    lineHeight: "22px",
                    paddingLeft: 7,
                    boxSizing: "border-box",
                    fontWeight: 600,
                  }}
                >
                  OK
                </div>
              )}

              <div
                style={{
                  float: "left",
                  position: "relative",
                  aspectRatio: 1.3,
                  width: "100%",
                }}
              >
                <img
                  src="/sparks.png"
                  alt="Sparks"
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: "calc(50% - 28px)",
                    top: "calc(50% - 18px)",
                    width: 56,
                    height: 56,
                    objectFit: "contain",
                  }}
                />
              </div>

              <div
                className="shadow1"
                title={`${sku.price} Sparks`}
                style={{
                  float: "left",
                  paddingLeft: 5,
                  paddingRight: 5,
                  margin: 3,
                  width: "calc(100% - 6px)",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "center",
                  overflow: "hidden",
                  boxSizing: "border-box",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  background: "#341a0d",
                  marginBottom: 0,
                  lineHeight: "24px",
                }}
              >
                {sku.price} Sparks
              </div>

              <div
                style={{
                  float: "left",
                  margin: 3,
                  width: "calc(100% - 6px)",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  background: "#7e4123",
                  lineHeight: "24px",
                  marginTop: 0,
                  marginBottom: 6,
                }}
              >
                <img
                  src="/runes.png"
                  alt=""
                  draggable={false}
                  style={{
                    width: 21,
                    height: 21,
                    objectFit: "contain",
                  }}
                />

                <span className="shadow1">{sku.amount}</span>
              </div>
            </button>
          );
        })}
      </div>
    )}
  </div>
)}

     {/* RUNES */}
{view === "coin_2" && (
  <div style={{ float: "left", marginLeft: -10, marginRight: -10, marginTop: -5 }}>
    <ShopHubTitle2>Buy Runes</ShopHubTitle2>

    {sparkSkus.length === 0 && (
      <div className="text-center text-muted py-4" style={{ color: "#fff" }}>
        No rune packs configured.
      </div>
    )}

    {sparkSkus.length > 0 && (
      <div
        className="modal_body modal_body_2"
        style={{
          display: "grid",
          paddingBottom: 5,
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
          boxSizing: "border-box",
        }}
      >
        {sparkSkus.map((sku, idx) => {
          const isChecked = selectedSparksIdx === idx;
          const rawPrice = Number(sku.price || 0);
          const discountedPrice = Number(sku.discountStars || 0);
          const finalPrice = discountedPrice > 0 ? discountedPrice : rawPrice;
          const hasDiscount = discountedPrice > 0 && discountedPrice < rawPrice;

          return (
            <button
              key={`runes-${idx}`}
              type="button"
              onClick={() => {
  const skuId = String(
    sku.skuId ??
    sku.sku_id ??
    sku.id ??
    ""
  ).trim();

  setSelectedSparksIdx(idx);

 const priceTon = Number(
  sku.priceTon ??
  sku.price_ton ??
  sku.tonPrice ??
  sku.ton_price ??
  0
);

setCurrencyToBuy({
  type: "runes",
  idx,

  sku: {
    ...sku,
    skuId,
  },

  skuId,
  title: "Runes",
  amount: sku.amount,

  price: finalPrice,
  oldPrice: hasDiscount ? rawPrice : null,

  priceTon,

  itemIcon: "/runes.png",
  priceIcon: "/tstar.svg",
  priceLabel: "Telegram Stars",
});

  setConfirmCurrencyOpen(true);
}}
              style={{
                width: "100%",
                boxSizing: "border-box",
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                borderRadius: 10,
                backgroundImage: 'url("/v2/item.png")',
                backgroundRepeat: "repeat",
                backgroundSize: "100% 100%",
                backgroundPosition: "center center",
                border: 0,
                padding: 0,
              }}
            >
              {hasDiscount && (
                <div
                  className="shadow1"
                  style={{
                    backgroundImage: 'url("/v2/discount.png")',
                    backgroundRepeat: "repeat",
                    backgroundSize: "100%",
                    backgroundPosition: "center",
                    position: "absolute",
                    width: 42,
                    height: 24,
                    top: 10,
                    right: 3,
                    zIndex: 5,
                    color: "#fff",
                    fontSize: 12,
                    lineHeight: "22px",
                    paddingLeft: 7,
                    boxSizing: "border-box",
                    fontWeight: 600,
                  }}
                >
                  SALE
                </div>
              )}

              <div
                style={{
                  float: "left",
                  position: "relative",
                  aspectRatio: 1.3,
                  width: "100%",
                }}
              >
                <img
                  src="/runes.png"
                  alt="Runes"
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: "calc(50% - 28px)",
                    top: "calc(50% - 18px)",
                    width: 56,
                    height: 56,
                    objectFit: "contain",
                  }}
                />
              </div>

              <div
                className="shadow1"
                title={`${sku.amount} Runes`}
                style={{
                  float: "left",
                  paddingLeft: 5,
                  paddingRight: 5,
                  margin: 3,
                  width: "calc(100% - 6px)",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "center",
                  overflow: "hidden",
                  boxSizing: "border-box",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  background: "#341a0d",
                  marginBottom: 0,
                  lineHeight: "24px",
                }}
              >
                {sku.amount} Runes
              </div>

              <div
                style={{
                  float: "left",
                  margin: 3,
                  width: "calc(100% - 6px)",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  background: "#7e4123",
                  lineHeight: "24px",
                  marginTop: 0,
                  marginBottom: 6,
                }}
              >
                {hasDiscount && (
                  <span
                    className="shadow1"
                    style={{
                      color: "rgba(255,255,255,0.72)",
                      fontSize: 15,
                      textDecoration: "line-through",
                    }}
                  >
                    {rawPrice}
                  </span>
                )}

                <img
                  src="/tstar.svg"
                  alt=""
                  draggable={false}
                  style={{
                    width: 21,
                    height: 21,
                    objectFit: "contain",
                  }}
                />

                <span className="shadow1">{finalPrice}</span>
              </div>
            </button>
          );
        })}
      </div>
    )}

  </div>
)}


      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal_box">

        <div className="modal_title">OOPS!<button onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage("");
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Error"
              style={{
                height: "80px",
                marginBottom: "10px",
                transform: "rotate(180deg)",
              }}
            />
            <div className="shadow2" style={{fontWeight: 600, fontSize: 24, marginBottom: "10px" }}>
              Transaction Failed
            </div>
            <div className="info"
              style={{
                textTransform: "capitalize",
                fontSize: "18px",
              }}
            >
              {errorMessage}
            </div>
            </div>
            <div className="modal_footer">
            <button
  className="game-button green-button" style={{marginRight: 10}}
  onClick={() => {
    setShowErrorModal(false);
    setErrorMessage("");
    setShopView("coin_2"); // go to Buy Runes tab immediately
  }}
>
  Buy Runes
</button>
              <button className="game-button red-button"
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage("");
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {skinPreviewOpen && (
  <div className="modal-backdrop" onClick={() => setSkinPreviewOpen(false)}>
    <motion.div
      className="modal fancy-success-modal"
      onClick={(e) => e.stopPropagation()}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 280, damping: 20 }}
      style={{ paddingTop: 18 }}
    >
      <h2 style={{ color: "#ffbb01", marginTop: 10, marginBottom: 10 }}>
        {skinPreviewItemName || skinPreviewSeason} Preview
      </h2>

      <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 14px" }}>
        {renderSkinPreviewThumb(demoPreviewCard, skinPreviewSeason)}
      </div>

      <div className="modal-footer">
        <button onClick={() => setSkinPreviewOpen(false)}>Close</button>
      </div>
    </motion.div>
  </div>
)}

{namePreviewOpen && namePreviewItem && (
  <div
    className="modal-backdrop"
    onClick={() => {
      setNamePreviewOpen(false);
      setNamePreviewItem(null);
    }}
  >
    <motion.div
      className="modal fancy-success-modal"
      onClick={(e) => e.stopPropagation()}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 280, damping: 20 }}
      style={{
        padding: "14px 18px 16px",
        maxWidth: 340,
        width: "86%",
        minHeight: "auto",
      }}
    >
      <h2
        style={{
          color: "#ffbb01",
          marginTop: 2,
          marginBottom: 4,
          fontSize: 30,
          lineHeight: "34px",
        }}
      >
        {namePreviewItem.name}
      </h2>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "8px 0 10px",
          minHeight: 80,
        }}
      >
        {getNameCategory(namePreviewItem) === "styles" ? (
          renderNameStyleSample(namePreviewItem, 28)
        ) : (
          <img
            src={getNamePreviewImage(namePreviewItem)}
            alt={namePreviewItem.name}
            style={{
              maxWidth: 170,
              maxHeight: 75,
              objectFit: "contain",
              imageRendering: "auto",
              filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.6))",
            }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/pack.png";
            }}
          />
        )}
      </div>

      <div className="modal-footer" style={{ marginTop: 4 }}>
        <button
          onClick={() => {
            setNamePreviewOpen(false);
            setNamePreviewItem(null);
          }}
        >
          Close
        </button>
      </div>
    </motion.div>
  </div>
)}
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal_box">

        <div className="modal_title">Success!<button className="modal_close" onClick={() => {
      setShowSuccessModal(false);
      setPurchaseType(null);
      setPurchasedAmount(null);
      setPurchasedCard(null);
      setPurchasedItem(null);
      setTonReceipt(null);
    }}/></div>

          <motion.div
            className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >


          <div className="modal_inside">


            <img
                src="/finger.png"
                alt="Success"
                style={{ height: "80px", marginBottom: "10px" }}
              />

            <div className="shadow2" style={{fontWeight: 600, fontSize: 21,marginBottom: "10px" }}>
              Transaction Completed!
            </div>
            <div>
              <div className="info">

                              {purchaseType === "side" && (
                  <>
                    You have switched to the{" "}
                    <strong style={{ color: "#09BDFF", textTransform: "capitalize" }}>
                      {user?.side}
                    </strong>{" "}
                    side!
                    <br />
                    <span style={{ fontSize: "16px" }}>
                      May your new allies not check your old chat logs.
                    </span>
                  </>
                )}

                {purchaseType === "card" && purchasedCard && (
                  <>
                    <div className="card-block">
                      {Object.entries(purchasedCard.character).map(
                        ([part, data]) => (
                          <img
                            key={part}
                            src={`/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`}
                            alt={part}
                          />
                        )
                      )}
                      <div className="card-name">{purchasedCard.name}</div>
                    </div>
                    <br /><br />
                    <span
                      style={{
                        textTransform: "capitalize",
                        lineHeight: "42px",
                        fontSize: "17px",
                      }}
                    >
                      You received a new{" "}
                      <strong style={{ color: "#09BDFF" }}>
                        {purchasedCard.rarity} card
                      </strong>
                      !
                    </span>
                    <br />
                    <strong>Attack:</strong> {purchasedCard.attack} |{" "}
                    <strong>Defense:</strong> {purchasedCard.defense} |{" "}
                    <strong>Health:</strong> {purchasedCard.health}
                    <br />
                    <strong>Abilities:</strong>{" "}
                    {purchasedCard.abilities.join(", ") || "None"}
                  </>
                )}
                {purchaseType === "runes" && purchasedAmount && (
  <>
    <strong
      style={{
        color: "#09BDFF",
        fontSize: 24,
      }}
    >
      {purchasedAmount} Runes
    </strong>

    <br />

    Successfully Added To Your Account!

    {tonReceipt && (
      <div
        style={{
          marginTop: 15,
          padding: "11px 12px",
          borderRadius: 7,
          background:
            "rgba(52, 26, 13, 0.72)",
          fontSize: 14,
          lineHeight: "22px",
          textAlign: "left",
        }}
      >
        <div>
          <strong>Status:</strong>{" "}
          <span style={{ color: "#67e36f" }}>
            Confirmed
          </span>
        </div>

        <div>
          <strong>Paid:</strong>{" "}
          {tonReceipt.amountTon} TON
        </div>

        {tonReceipt.sender && (
          <div>
            <strong>From:</strong>{" "}
            {shortenTonValue(
              tonReceipt.sender
            )}
          </div>
        )}

        {tonReceipt.destination && (
          <div>
            <strong>To:</strong>{" "}
            {shortenTonValue(
              tonReceipt.destination
            )}
          </div>
        )}

        <div>
          <strong>Order:</strong>{" "}
          {shortenTonValue(
            tonReceipt.orderId
          )}
        </div>

        {tonReceipt.newRuneBalance !== null &&
          tonReceipt.newRuneBalance !== undefined && (
            <div>
              <strong>New Balance:</strong>{" "}
              {Number(
                tonReceipt.newRuneBalance
              ).toLocaleString()}{" "}
              Runes
            </div>
          )}
      </div>
    )}

    {tonReceipt?.explorerUrl && (
      <a
        href={tonReceipt.explorerUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="game-button blue-button"
        style={{
          display: "inline-block",
          marginTop: 14,
          padding: "0 15px",
          lineHeight: "42px",
          textDecoration: "none",
          color: "#fff",
        }}
      >
        VIEW ON BLOCKCHAIN
      </a>
    )}
  </>
)}
                {purchaseType === "sparks" && purchasedAmount && (
  <>
    <strong>{purchasedAmount} Sparks</strong> Successfully Added
    To Your Account!
  </>
)}
              
            


{purchaseType === "item" && purchasedItem && (


  <>
    <span style={{ color: "#FFAC01", fontSize: "20px" }}>
      You Purchased:
    </span>
    <br />
    <strong style={{ textTransform: "capitalize", color: "#09BDFF", fontSize: "24px" }}>
      {purchasedItem.name}
    </strong>
    <span className="shadow1" style={{ float: "left", width: "100%", fontSize: "12px", marginTop: 5, fontWeight: 600}}>
      {["tapi", "runes", "sparks","ticket"].includes(String(purchasedItem.type || "").trim().toLowerCase())
        ? "This purchase has been added to your balance."
        : "This item has been added to your inventory."}
    </span>
  </>
)}
</div>
</div>
</div>
            <div className="modal_footer">
  {purchaseType === "item" &&
  purchasedItem &&
  !["tapi", "runes", "sparks","ticket"].includes(String(purchasedItem.type || "").trim().toLowerCase()) && (
    <button className="game-button green-button"
      onClick={goToInventoryItems}
      style={{ marginRight: 10}}
    >
      Inventory
    </button>
  )}

  <button
  className="game-button red-button"
    onClick={() => {
      setShowSuccessModal(false);
      setPurchaseType(null);
      setPurchasedAmount(null);
      setPurchasedCard(null);
      setPurchasedItem(null);
      setTonReceipt(null);
    }}
  >
    CLOSE
  </button>
</div>
          </motion.div>
        </div>
      )}


      {confirmItemOpen && itemToBuy && !showErrorModal && !showSuccessModal && (
  <div className="modal_box">
  <div className="modal_title">Confirm?<button className="modal_close" onClick={() => {
            setConfirmItemOpen(false);
            setItemToBuy(null);
          }}/></div>
    <motion.div
      className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
      
    >



      <div className="modal_inside">

      {renderConfirmItemPreview(itemToBuy)}

      <div
  className="shadow2"
  style={{
    fontSize: 28,
    fontWeight: 600,
    marginBottom: 10,
    marginTop: itemHasConfirmPreview(itemToBuy) ? 0 : 10,
  }}
>
  Confirm Purchase?
</div>

{!confirmPriceInfo.priceIsTelegramStars && (
  <div className="info">
    <div
      style={{
        color: "#ddd",
        fontSize: 18,
        textShadow: "1px 1px #000",
      }}
    >
      Quantity
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        margin: "0px 0",
      }}
    >
      <button
        type="button"
        onClick={() =>
          setItemBuyQuantity((q) => Math.max(1, q - 1))
        }
        disabled={itemBuyQuantity <= 1}
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          fontSize: 26,
          fontWeight: "bold",
        }}
      >
        -
      </button>

      <div
        style={{
          minWidth: 90,
          textAlign: "center",
          color: "#ffbb01",
          fontSize: 26,
          fontWeight: "bold",
          textShadow: "1px 1px #000",
        }}
      >
        {itemBuyQuantity}
      </div>

      <button
        type="button"
        onClick={() =>
          setItemBuyQuantity((q) => Math.min(10, q + 1))
        }
        disabled={itemBuyQuantity >= 10}
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          fontSize: 26,
          fontWeight: "bold",
        }}
      >
        +
      </button>
    </div>
  </div>
)}
      <p
       className="info shadow1"
        style={{
          fontSize: 18,
          color: "#ddd",
          lineHeight: "28px",
        }}
      >
        Buy{" "}
{!confirmPriceInfo.priceIsTelegramStars && (
  <strong
    style={{
      color: "#ffdf5f",
    }}
  >
    x{itemBuyQuantity}
  </strong>
)}
<br />
for{" "}

<span style={{ color: "#ffbb01", fontWeight: "bold" }}>
  {confirmPriceInfo.hasDiscount && (
    <>
      <span
        style={{
          textDecoration: "line-through",
          opacity: 0.65,
          marginRight: 6,
        }}
      >
{Number(
  confirmPriceInfo.oldPrice *
    (confirmPriceInfo.priceIsTelegramStars
      ? 1
      : itemBuyQuantity)
).toLocaleString()}
      </span>
    </>
  )}

  {confirmTotalPrice.toLocaleString()}{" "}
{confirmPriceInfo.currencyLabel}
</span>
?
      </p>

      {String(itemToBuy?.type || "").toLowerCase() === "magic_potion" && (
  <div
    className="info"
  >
    <div
      className="shadow1"
      style={{
        fontSize: 12,
        lineHeight: "18px",
        color: "#ddd",
        textAlign: "center",
        marginBottom: 10,
      }}
    >
      This potion converts{" "}
      <strong style={{ color: "#ffbb01" }}>
        {Number(itemToBuy.potion_sparks_cost || 0).toLocaleString()} Sparks
      </strong>{" "}
      into Runes.
    </div>

    <div
      className="shadow1"
      style={{
        fontSize: 13,
        color: "#fff",
        marginBottom: 7,
        fontWeight: 600,
      }}
    >
      Possible Results:
    </div>

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      {(Array.isArray(itemToBuy.potion_rewards)
        ? itemToBuy.potion_rewards
        : []
      )
        .slice()
        .sort(
          (a, b) =>
            Number(b?.runes || 0) -
            Number(a?.runes || 0)
        )
        .map((reward, index) => (
          <div
            key={`${reward.runes}-${reward.chance}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "rgba(0,0,0,0.28)",
              border: "1px solid rgba(255,193,7,0.28)",
              borderRadius: 6,
              padding: "6px 10px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <img
                src="/runes.png"
                alt=""
                style={{
                  width: 24,
                  height: 24,
                  objectFit: "contain",
                }}
              />

              <span
                className="shadow1"
                style={{
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {Number(
                  reward.runes || 0
                ).toLocaleString()}{" "}
                Runes
              </span>
            </div>

            <span
              className="shadow1"
              style={{
                color: "#ffbb01",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {Number(reward.chance || 0)}%
            </span>
          </div>
        ))}
    </div>
  </div>
)}

   {String(itemToBuy?.type || "").toLowerCase() === "chest" &&
  getChestRewards(itemToBuy).length > 0 && (
  <div className="info">
    <div
      style={{
        width: "100%",
      }}
    >

      {/* FRASE FIXA */}
      <div
        className="shadow1"
        style={{
          fontSize: 13,
          lineHeight: "15px",
          color: "#ddd",
          textAlign: "center",
          marginBottom: 8,
        }}
      >
        At least one reward is guaranteed.<br/>You may also receive additional rewards.
      </div>


      {/* REWARDS - SWIPE HORIZONTAL */}
      
      <div
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 6,
            width: "max-content",
          }}
        >
          {getChestRewards(itemToBuy).map((reward) => (
            <div
              key={reward.key}
              style={{
                height: 42,
                padding: "0 10px 0 6px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 3,
                borderRadius: 7,
                background: "rgba(0, 0, 0, 0.28)",
                border: "1px solid rgba(255, 193, 7, 0.28)",
              }}
            >
              {reward.icon && (
                <img
                  src={reward.icon}
                  alt=""
                  style={{
                    width: 30,
                    height: 30,
                    objectFit: "contain",
                  }}
                />
              )}

              <span
                className="shadow1"
                style={{
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                {reward.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  )}

      </div>

      <div className="modal_footer">
        <button className="game-button green-button" style={{marginRight: 10}}
  onClick={() => {
    const priceInfo = getItemPriceInfo(itemToBuy);

    if (priceInfo.priceIsTelegramStars) {
      purchaseSelectedItemWithStars(itemToBuy);
      return;
    }

    purchaseSelectedItem(itemToBuy.id);
  }}
>
  Confirm
</button>

        <button className="game-button red-button"
          onClick={() => {
            setConfirmItemOpen(false);
            setItemToBuy(null);
          }}
        >
          CANCEL
        </button>
      </div>
    </motion.div>
  </div>
)}


{confirmCurrencyOpen &&
  currencyToBuy &&
  !showErrorModal &&
  !showSuccessModal && (
    <div className="modal_box">
      <div className="modal_title">
        {tonPaymentBusy
          ? "TON Payment"
          : "Confirm?"}

        {!tonPaymentBusy && (
          <button
            className="modal_close"
            onClick={() => {
              setConfirmCurrencyOpen(false);
              setCurrencyToBuy(null);
            }}
          />
        )}
      </div>

      <motion.div
        className="modal_body"
        initial={{
          scale: 0.8,
          opacity: 0,
        }}
        animate={{
          scale: 1,
          opacity: 1,
        }}
        exit={{
          scale: 0.92,
          opacity: 0,
        }}
      >
        <div className="modal_inside">
         {tonPaymentBusy ? (
  <>
    <img
      src="/ton.svg"
      alt="TON"
      draggable={false}
      style={{
        height: 70,
        width: 70,
        objectFit: "contain",
        marginBottom: 10,
      }}
    />

    <div
      className="shadow2"
      style={{
        fontSize: 25,
        fontWeight: 600,
        marginBottom: 10,
      }}
    >
      {tonPaymentStage === "wallet"
        ? "Confirm In Your Wallet"
        : "Payment Sent"}
    </div>

    <div
      className="info"
      style={{
        fontSize: 15,
        lineHeight: "25px",
      }}
    >
      {tonPaymentStage === "wallet" ? (
        <>
          Approve the transaction in your wallet.
        </>
      ) : (
        <>
          Your payment was sent successfully.
          <br />
          Waiting for blockchain confirmation...
        </>
      )}
    </div>
  </>
) : (
            <>
              <img
                src={currencyToBuy.itemIcon}
                alt={currencyToBuy.title}
                style={{
                  height: 80,
                  objectFit: "contain",
                }}
              />

              <div
                className="shadow2"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 10,
                  marginTop: 10,
                }}
              >
                Confirm Purchase?
              </div>

              <p
  className="info shadow1"
  style={{
    fontSize: 18,
    color: "#ddd",
    lineHeight: "28px",
  }}
>
  Buy{" "}
  <strong
    style={{
      color: "#09BDFF",
      textTransform: "capitalize",
    }}
  >
    {currencyToBuy.amount}{" "}
    {currencyToBuy.title}
  </strong>

  <br />

  for{" "}

  <span
  style={{
    color: "#ffbb01",
    fontWeight: "bold",
  }}
>
  {currencyToBuy.oldPrice && (
    <span
      style={{
        textDecoration: "line-through",
        opacity: 0.65,
        marginRight: 6,
      }}
    >
      {currencyToBuy.oldPrice}
    </span>
  )}

  {currencyToBuy.price}{" "}
  {currencyToBuy.priceLabel}
</span>

{"?"}
</p>
            </>
          )}
        </div>

        {!tonPaymentBusy && (
          <div
            className="modal_footer"
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 10,
            }}
          >
            {currencyToBuy.type ===
              "sparks" && (
              <button
                className="game-button green-button"
                onClick={() => {
                  purchaseRunes();
                  setConfirmCurrencyOpen(false);
                  setCurrencyToBuy(null);
                }}
              >
                CONFIRM
              </button>
            )}

            {currencyToBuy.type ===
              "runes" && (
              <>
                <button
                  className="game-button green-button" style={{paddingLeft: 10, paddingRight: 10}}
                  onClick={
                    purchaseSparks_stars
                  }
                >
                  PAY WITH TELEGRAM STARS
                </button>

                <button
                  className="game-button blue-button" style={{display: "none", paddingLeft: 10, paddingRight: 10}}
                  onClick={() => {
                    purchaseRunesWithTon(
                      currencyToBuy
                    );
                  }}
                >
                  {tonWallet
                    ? "PAY WITH TON"
                    : "ADD WALLET"}
                </button>
              </>
            )}
          </div>
        )}
      </motion.div>
    </div>
  )}

            {/* Confirm Side Change Modal */}
      {showSideModal && sideTarget && !showErrorModal && !showSuccessModal && (
        <div className="modal-backdrop">
          <motion.div
            className="modal fancy-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <h2 style={{ color: "#ffbb01", marginBottom: "10px" }}>
              Switch To The {sideTarget === "sun" ? "Sun" : "Moon"} Side?
            </h2>

            <img src={nextSide === "sun" ? "/sun_tap.png" : "/moon_tap.png"} alt="Switch Side" style={{marginTop: 15,marginBottom: 15,height: 120}}/>

            <div
              style={{
                textShadow: "1px 1px #000",
                fontSize: "20px",
                color: "#ddd",
                lineHeight: "26px",
              }}
            >
              This will cost{" "}
<span style={{ color: "#ffbb01", fontWeight: "bold" }}>
  {sideUseBypass
  ? `${SIDE_CHANGE_BYPASS_RUNES_PRICE} Runes`
  : `${changeSidePrice} Sparks`}
</span>
              .<br />

              <span style={{ fontSize: "16px", opacity: 0.9 }}>
                The{" "}
                <b style={{ textTransform: "capitalize" }}>{currentSide}</b> side
                will remember this betrayal.
              </span>

              <div
                style={{
                  fontSize: "16px",
                  opacity: 0.9,
                  color: "#ffbb01",
                  marginTop: 5,
                }}
              >
                IMPORTANT:
              </div>

              <div style={{ fontSize: "16px", opacity: 0.9 }}>
                When you switch sides, only your side changes.
                <br />
                Previously purchased cards remain on the side they were earned and are not moved.
              </div>
            </div>
            <div className="modal-footer">
              <button
  onClick={() => changeSide({ bypassCooldown: sideUseBypass })}
  disabled={sideLoading}
>
  {sideLoading ? "Switching..." : "Confirm Switch"}
</button>
              <button
  onClick={() => {
    setShowSideModal(false);
    setSideTarget(null);
    setSideUseBypass(false);
  }}
>
  Cancel
</button>
            </div>
          </motion.div>
        </div>
      )}


      {/* Confirm Pack Modal */}
      {showPackModal && selectedPack && (
        <div className="modal_box">

  <div className="modal_title">Confirm?<button onClick={() => {
                  setShowPackModal(false);
                  setSelectedPack(null);
                }} className="modal_close"/></div>

          <motion.div
            className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            
          >

            <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Confirm"
              style={{ height: "80px", marginBottom: "10px" }}
            />
            <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: "10px" }}>
              Confirm Purchase?
            </div>

            <p
            className="info"
              style={{
                fontSize: "18px",
              }}
            >
              Buy A{" "}
              <span style={{ color: "#09BDFF" }}>
                {selectedPack.rarity} Card
              </span>{" "}
              For{" "}
              <span style={{ color: "#ffbb01" }}>
                {selectedPack.price} Runes
              </span>
            </p>
            </div>
            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}} onClick={confirmPurchase}>Confirm</button>
              <button className="game-button red-button"
                onClick={() => {
                  setShowPackModal(false);
                  setSelectedPack(null);
                }}
              >
                CANCEL
              </button>
            </div>
                    </motion.div>
          </div>
      )}

          <DiscountOfferModal
  user={user}
  guardedNavigate={(to) => onNavigate?.(to)}
  blocked={false}
  onPurchased={(u) => setUser((prev) => ({ ...prev, ...u }))}
/>

    </div>

  );
}

export default Shop;
